import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../Utils/Axios";
import * as XLSX from "xlsx";

function BankKhata() {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]); // This will be payment methods that are banks
  const [selectedBank, setSelectedBank] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [ledgerParties, setLedgerParties] = useState([]); // Store ledger parties to lookup names from IDs
  const [selectedTransactions, setSelectedTransactions] = useState([]); // Selected transaction IDs
  const [isDeleting, setIsDeleting] = useState(false); // Loading state for bulk delete
  const [bills, setBills] = useState([]); // Store bills to fetch bill numbers for SettledBill transactions
  const [company, setCompany] = useState(null); // Store company data
  const printRef = useRef(null); // Ref for print/PDF container
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false); // Loading state for PDF generation

  // Helper function to extract name and type from description
  // Format: "روزنامچہ - [Name/ID] - رقم: [Amount]" or "Roznamacha - [Name/ID] - Amount: [Amount]"
  const extractNameFromDescription = (description, descriptionUrdu) => {
    const desc = descriptionUrdu || description || '';
    if (!desc) return '-';

    let extractedValue = '';

    // Try to extract name/ID from format: "روزنامچہ - [Name/ID] - رقم: [Amount]"
    const roznamachaMatch = desc.match(/روزنامچہ\s*[-–]\s*([^-–]+?)\s*[-–]\s*رقم/);
    if (roznamachaMatch && roznamachaMatch[1]) {
      extractedValue = roznamachaMatch[1].trim();
    } else {
      // Try to extract from General Voucher Urdu format:
      // "جنرل واؤچر - [to/from ...] [Name/ID] - رقم: [Amount]"
      const voucherMatch = desc.match(/جنرل\s*واؤچر\s*[-–]\s*(?:کو|سے)?\s*([0-9a-fA-F]{24}|[^-–]+?)\s*[-–]\s*رقم/);
      if (voucherMatch && voucherMatch[1]) {
        extractedValue = voucherMatch[1].trim();
      } else {
        // Try English format: "Roznamacha - [Name/ID] - Amount: [Amount]"
        const roznamachaEnMatch = desc.match(/Roznamacha\s*[-–]\s*([^-–]+?)\s*[-–]\s*Amount/);
        if (roznamachaEnMatch && roznamachaEnMatch[1]) {
          extractedValue = roznamachaEnMatch[1].trim();
        } else {
          // Try English General Voucher format: "General Voucher - to/from [Name/ID] - Amount: [Amount]"
          const voucherEnMatch = desc.match(/General\s*Voucher\s*[-–]\s*(?:to|from)?\s*([0-9a-fA-F]{24}|[^-–]+?)\s*[-–]\s*Amount/i);
          if (voucherEnMatch && voucherEnMatch[1]) {
            extractedValue = voucherEnMatch[1].trim();
          } else {
            // Try simpler pattern: anything between "روزنامچہ -" and "- رقم"
            const simpleMatch = desc.match(/روزنامچہ\s*[-–]\s*(.+?)\s*[-–]\s*رقم/);
            if (simpleMatch && simpleMatch[1]) {
              extractedValue = simpleMatch[1].trim();
            } else if (desc.includes('روزنامچہ')) {
              // Fallback: get text after first dash
              const parts = desc.split(/[-–]/);
              if (parts.length >= 2) {
                extractedValue = parts[1].trim().replace(/\s*رقم.*$/, '').trim();
              }
            }
          }
        }
      }
    }

    if (!extractedValue) return '-';

    // Check if extracted value is a MongoDB ObjectId (24 hex characters)
    const isObjectId = /^[0-9a-fA-F]{24}$/.test(extractedValue);

    if (isObjectId && ledgerParties.length > 0) {
      // Look up the party name from the ID
      const party = ledgerParties.find(p => {
        const partyId = p._id?.toString() || p._id;
        return partyId === extractedValue || partyId?.toString() === extractedValue;
      });

      if (party) {
        // Return the actual name with type indicator (Urdu if available, otherwise English)
        const name = party.nameUrdu || party.name || extractedValue;
        const typeLabel = party.type === 'supplier'
          ? (companyLanguage === 'ur' ? ' (سپلائر)' : ' (Supplier)')
          : party.type === 'customer'
            ? (companyLanguage === 'ur' ? ' (گاہک)' : ' (Customer)')
            : '';
        console.log(`Found party name for ID ${extractedValue}: ${name}${typeLabel}`);
        return `${name}${typeLabel}`;
      }
    }

    // If not an ID or party not found, return the extracted value as is
    return extractedValue;
  };
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showGeneralVoucherModal, setShowGeneralVoucherModal] = useState(false);
  const [generalVoucherForm, setGeneralVoucherForm] = useState({
    date: new Date().toISOString().split('T')[0],
    fromCategory: 'bank', // bank | supplier | customer | receivable | payable | expense
    fromId: '', // bankId or partyId (depends on category)
    toCategory: 'supplier',
    toId: '',
    expenseType: 'cargo', // used only when selecting company expense ledger
    amount: '',
    description: '',
    descriptionUrdu: ''
  });
  const [transactionForm, setTransactionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'deposit',
    amount: '',
    fromBank: '', // Source bank
    toBank: '', // Destination bank
    paymentMethod: '',
    description: '',
    descriptionUrdu: ''
  });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showInterBankModal, setShowInterBankModal] = useState(false);
  const [showInterBankExcelView, setShowInterBankExcelView] = useState(false);
  const [showBankHistory, setShowBankHistory] = useState(false);
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  const [interBankTransactions, setInterBankTransactions] = useState([]);
  const [bankHistoryTransactions, setBankHistoryTransactions] = useState([]);
  const [interBankForm, setInterBankForm] = useState({
    date: new Date().toISOString().split('T')[0],
    fromBank: '',
    toBank: '',
    amount: '',
    description: '',
    descriptionUrdu: ''
  });
  const [addBalanceForm, setAddBalanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    descriptionUrdu: ''
  });
  const [bankBalances, setBankBalances] = useState({}); // Store calculated balances for each bank

  const mapCategoryToKind = (category) => {
    if (category === 'bank') return 'bank';
    if (category === 'expense') return 'expense';
    return 'khata'; // supplier/customer/receivable/payable
  };

  const getAccountFromForm = (side) => {
    const category = side === 'from' ? generalVoucherForm.fromCategory : generalVoucherForm.toCategory;
    const id = side === 'from' ? generalVoucherForm.fromId : generalVoucherForm.toId;
    const kind = mapCategoryToKind(category);
    if (kind === 'expense') return { kind: 'expense', id: 'company' };
    return { kind, id };
  };

  const getAccountLabel = (kind, id) => {
    if (kind === 'bank') {
      const b = banks.find((x) => (x._id?.toString() || x._id) === (id?.toString() || id));
      return isUrdu ? (b?.bankNameUrdu || b?.nameUrdu || b?.bankName || b?.name || '-') : (b?.bankName || b?.name || '-');
    }
    if (kind === 'khata') {
      const p = ledgerParties.find((x) => (x._id?.toString() || x._id) === (id?.toString() || id));
      return isUrdu ? (p?.nameUrdu || p?.name || '-') : (p?.name || p?.nameUrdu || '-');
    }
    if (kind === 'expense') {
      return isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger';
    }
    return '-';
  };

  const handleGeneralVoucherSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const amountNum = parseFloat(generalVoucherForm.amount);
      const from = getAccountFromForm('from');
      const to = getAccountFromForm('to');

      // Require IDs where needed
      const fromNeedsId = generalVoucherForm.fromCategory !== 'expense';
      const toNeedsId = generalVoucherForm.toCategory !== 'expense';

      if (!generalVoucherForm.fromCategory || (fromNeedsId && !generalVoucherForm.fromId)) {
        toast.error(isUrdu ? 'براہ کرم سورس منتخب کریں' : 'Please select source');
        return;
      }
      if (!generalVoucherForm.toCategory || (toNeedsId && !generalVoucherForm.toId)) {
        toast.error(isUrdu ? 'براہ کرم منزل منتخب کریں' : 'Please select destination');
        return;
      }
      if (isNaN(amountNum) || amountNum <= 0) {
        toast.error(isUrdu ? 'براہ کرم درست رقم درج کریں' : 'Please enter a valid amount');
        return;
      }

      // If Company Expense ledger is used: only allow Bank -> Expense (withdraw)
      if (from.kind === 'expense' || to.kind === 'expense') {
        if (!(from.kind === 'bank' && to.kind === 'expense')) {
          toast.error(isUrdu ? 'کمپنی اخراجات کیلئے صرف بینک سے اخراجات (Bank → Expense) سپورٹ ہے' : 'Only Bank → Expense is supported for company expenses');
          return;
        }
        if (!generalVoucherForm.expenseType) {
          toast.error(isUrdu ? 'اخراجات کی قسم منتخب کریں' : 'Select expense type');
          return;
        }
        const bankId = from.id;
        const bankName = getAccountLabel('bank', bankId);
        const descEn = generalVoucherForm.description || `Expense - ${amountNum}`;
        const descUr = generalVoucherForm.descriptionUrdu || `اخراجات - ${amountNum}`;

        const payload = {
          date: generalVoucherForm.date,
          travellingExpense: 0,
          refreshment: 0,
          cargo: 0,
          salaryExpense: 0,
          paymentMethod: bankName,
          description: descEn,
          descriptionUrdu: descUr
        };
        // put amount into chosen expense bucket
        payload[generalVoucherForm.expenseType] = amountNum;

        // Expense route auto-creates bank withdraw transaction; don't create bank tx separately
        await api.post(`/expense-ledger/company/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(isUrdu ? 'اخراجات محفوظ ہو گئے' : 'Expense saved');

        setShowGeneralVoucherModal(false);
        setGeneralVoucherForm({
          date: new Date().toISOString().split('T')[0],
          fromAccount: selectedBank?._id ? `bank:${selectedBank._id}` : '',
          toAccount: '',
          expenseType: 'cargo',
          amount: '',
          description: '',
          descriptionUrdu: ''
        });

        // Refresh
        await fetchPaymentMethods();
        await fetchAllBankBalances();
        if (selectedBank) {
          fetchBankTransactions(selectedBank._id);
        }
        return;
      }

      // Only allow Bank <-> Khata
      if (!from.kind || !to.kind || from.kind === to.kind) {
        toast.error(isUrdu ? 'براہ کرم ایک طرف بینک اور دوسری طرف کھاتہ منتخب کریں' : 'Select one Bank and one Khata');
        return;
      }

      const bankSide = from.kind === 'bank' ? from : to;
      const khataSide = from.kind === 'khata' ? from : to;
      const bankId = bankSide.id;
      const partyId = khataSide.id;

      const party = ledgerParties.find(p => (p._id?.toString() || p._id) === (partyId?.toString() || partyId));
      const bankName = getAccountLabel('bank', bankId);

      // Bank tx type: if money goes INTO bank => deposit, else withdraw
      // If FROM is bank => withdraw (bank -> khata), else deposit (khata -> bank)
      const bankTxType = from.kind === 'bank' ? 'withdraw' : 'deposit';

      // Ledger transaction type: reduce balance magnitude (settlement)
      // - receivable (positive): credit reduces
      // - payable (negative): debit reduces
      const ledgerTxType = party?.balanceType === 'payable' ? 'debit' : 'credit';

      const autoDescEn = `General Voucher - ${bankTxType === 'withdraw' ? 'to' : 'from'} ${partyId} - Amount: ${amountNum}`;
      const autoDescUr = `جنرل واؤچر - ${bankTxType === 'withdraw' ? 'کو' : 'سے'} ${partyId} - رقم: ${amountNum}`;

      // 1) Create bank transaction (deposit/withdraw)
      await api.post(`/bank/company/payment-method/transaction`, {
        paymentMethodId: bankId,
        date: generalVoucherForm.date,
        type: bankTxType,
        amount: amountNum,
        description: generalVoucherForm.description || autoDescEn,
        descriptionUrdu: generalVoucherForm.descriptionUrdu || autoDescUr
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 2) Create ledger (khata) transaction (settlement)
      await api.post(`/ledger/parties/${partyId}/transactions`, {
        date: generalVoucherForm.date,
        description: (generalVoucherForm.description || (isUrdu ? autoDescUr : autoDescEn)) + (bankName ? (isUrdu ? ` - بینک: ${bankName}` : ` - Bank: ${bankName}`) : ''),
        type: ledgerTxType,
        amount: amountNum
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(isUrdu ? 'جنرل واؤچر محفوظ ہو گیا' : 'General voucher saved');

      setShowGeneralVoucherModal(false);
      setGeneralVoucherForm({
        date: new Date().toISOString().split('T')[0],
        fromCategory: 'bank',
        fromId: selectedBank?._id || '',
        toCategory: 'supplier',
        toId: '',
        expenseType: 'cargo',
        amount: '',
        description: '',
        descriptionUrdu: ''
      });

      // Refresh
      await fetchPaymentMethods();
      await fetchAllBankBalances();
      if (selectedBank) {
        fetchBankTransactions(selectedBank._id);
      }
    } catch (error) {
      console.error("General voucher error:", error);
      const msg = error?.response?.data?.message || error?.message || 'Failed';
      toast.error(isUrdu ? `ناکام: ${msg}` : `Failed: ${msg}`);
    }
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get("/ledger/parties", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
      console.log('Fetched ledger parties for BankKhata:', response.data.parties?.length || 0);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchPaymentMethods();
    fetchLedgerParties();
    fetchBills();

    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setDateRange({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    });
  }, []);

  useEffect(() => {
    if (selectedBank) {
      fetchBankTransactions(selectedBank._id);
    }
  }, [selectedBank, bills]);

  // Default voucher "from bank" to currently selected bank
  useEffect(() => {
    if (selectedBank && !generalVoucherForm.fromId) {
      setGeneralVoucherForm((prev) => ({
        ...prev,
        fromCategory: 'bank',
        fromId: selectedBank._id
      }));
    }
  }, [selectedBank]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paymentMethods.length > 0) {
      fetchAllBankBalances();
    }
  }, [paymentMethods]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        navigate("/company/login");
        return;
      }
      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const companyData = response.data.company;
      setCompany(companyData);
      const lang = companyData?.websiteLanguage || companyData?.language || 'en';
      setCompanyLanguage(lang);
    } catch (error) {
      console.error("Fetch company error:", error);
    }
  };

  const fetchBills = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get("/bill/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data.bills || []);
    } catch (error) {
      console.error("Fetch bills error:", error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("companyToken");
      if (!token) {
        navigate("/company/login");
        return;
      }

      // Fetch payment methods
      const response = await api.get("/payment-method/active");
      const methods = response.data.paymentMethods || [];
      setPaymentMethods(methods);

      // Convert ALL payment methods to bank-like structure (each payment method gets its own khata)
      const banksList = methods.map((method, index) => ({
        _id: method._id,
        bankName: method.name,
        bankNameUrdu: method.nameUrdu || method.name,
        accountNumber: '',
        accountTitle: '',
        branchName: '',
        openingBalance: 0,
        isActive: method.isActive,
        paymentMethodId: method._id
      }));

      setBanks(banksList);
      if (banksList.length > 0 && !selectedBank) {
        setSelectedBank(banksList[0]);
      }
      if (banksList.length === 0) {
        console.log("No payment methods found");
        toast.info(companyLanguage === 'ur' ? 'کوئی ادائیگی کا طریقہ نہیں ملا' : "No payment methods found");
      }
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      toast.error(companyLanguage === 'ur' ? 'ادائیگی کے طریقے لوڈ کرنے میں ناکام' : "Failed to load payment methods");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBankTransactions = async (bankId) => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!selectedBank) return;

      // Ensure bills are loaded before processing transactions
      if (bills.length === 0) {
        console.log('Bills not loaded, fetching bills first...');
        await fetchBills();
      }

      // Use payment method name to find transactions (for recoveries/expenses)
      const bankName = selectedBank.bankName || selectedBank.bankNameUrdu;
      const bankNameUrdu = selectedBank.bankNameUrdu;

      // Get all recoveries for this company
      const allRecoveriesResponse = await api.get(`/recovery/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get all expenses for this company
      const allExpensesResponse = await api.get(`/expense-ledger/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter recoveries that used this payment method (deposits)
      const recoveries = (allRecoveriesResponse.data.recoveries || []).filter(r => {
        const paymentMethod = r.paymentMethod || '';
        return paymentMethod === bankName ||
          paymentMethod === bankNameUrdu ||
          paymentMethod === selectedBank.bankName ||
          paymentMethod === selectedBank.bankNameUrdu;
      });

      // Filter expenses that used this payment method (withdrawals)
      const expenses = (allExpensesResponse.data.expenses || []).filter(e => {
        const paymentMethod = e.paymentMethod || '';
        return paymentMethod === bankName ||
          paymentMethod === bankNameUrdu ||
          paymentMethod === selectedBank.bankName ||
          paymentMethod === selectedBank.bankNameUrdu;
      });

      // Get bank transactions from bank's ledger (using bankId)
      // Note: bankId here is actually a payment method ID in BankKhata
      let bankTransactions = [];
      try {
        // First try to get transactions from bank's specific ledger
        // This might fail if bankId is a payment method ID, not a Bank document ID
        const bankTransactionsResponse = await api.get(`/bank/company/${bankId}/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        bankTransactions = bankTransactionsResponse.data.transactions || [];
        console.log(`Fetched ${bankTransactions.length} transactions from bank ledger for ${selectedBank.bankName}`);
      } catch (txError) {
        console.log("Bank-specific endpoint failed, trying all transactions:", txError.message);
        // Fallback: get all transactions and filter by bankId or payment method
        try {
          const allBankTransactionsResponse = await api.get(`/bank/company/transactions/all`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allTransactions = allBankTransactionsResponse.data.transactions || [];
          console.log(`Fetched ${allTransactions.length} total transactions, filtering for ${selectedBank.bankName}`);

          bankTransactions = allTransactions.filter(tx => {
            // Check by bankId first (for transactions posted to bank ledger)
            // bankId might be payment method ID or Bank document ID
            if (tx.bankId) {
              const txBankId = tx.bankId.toString();
              const searchBankId = bankId.toString();
              const selectedBankId = selectedBank._id?.toString();

              if (txBankId === searchBankId || txBankId === selectedBankId) {
                console.log(`Transaction matched by bankId: ${txBankId}`);
                return true;
              }
            }

            // Check by payment method name (for SettledBill transactions)
            const txPaymentMethod = tx.paymentMethod || '';
            if (txPaymentMethod === bankName ||
              txPaymentMethod === bankNameUrdu ||
              txPaymentMethod === selectedBank.bankName ||
              txPaymentMethod === selectedBank.bankNameUrdu) {
              console.log(`Transaction matched by paymentMethod: ${txPaymentMethod}`);
              return true;
            }

            // Also check if transaction description contains roznamacha
            // For SettledBill transactions, they might not have paymentMethod set correctly
            const hasRoznamacha = (tx.description && (tx.description.includes('Roznamacha') || tx.description.includes('روزنامچہ'))) ||
              (tx.descriptionUrdu && tx.descriptionUrdu.includes('روزنامچہ'));

            if (hasRoznamacha) {
              // For roznamacha transactions, include them if they match ANY payment method name
              // This is because SettledBill transactions might not have the exact paymentMethod set
              const matchesPaymentMethod =
                (tx.description && (tx.description.includes(bankName) || tx.description.includes(selectedBank.bankName))) ||
                (tx.descriptionUrdu && (tx.descriptionUrdu.includes(bankNameUrdu) || tx.descriptionUrdu.includes(selectedBank.bankNameUrdu)));

              if (matchesPaymentMethod) {
                console.log(`Roznamacha transaction matched: ${tx.description || tx.descriptionUrdu}`);
                return true;
              }

              // If no payment method match but it's roznamacha and bankId is null or matches,
              // include it (SettledBill transactions might have bankId: null)
              if (!tx.bankId || tx.bankId.toString() === bankId.toString() || tx.bankId.toString() === selectedBank._id?.toString()) {
                console.log(`Roznamacha transaction included (bankId match or null): ${tx.description || tx.descriptionUrdu}`);
                return true;
              }
            }

            return false;
          });

          console.log(`Filtered to ${bankTransactions.length} transactions for ${selectedBank.bankName}`);
        } catch (fallbackError) {
          console.error("Error fetching all bank transactions:", fallbackError);
          bankTransactions = [];
        }
      }

      // Combine all transactions and sort by date
      const allTransactions = [];

      // Add deposits (recoveries)
      recoveries.forEach(recovery => {
        // Filter by date range if provided
        const recoveryDate = new Date(recovery.date);
        if (dateRange.startDate && recoveryDate < new Date(dateRange.startDate)) return;
        if (dateRange.endDate) {
          const endDate = new Date(dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (recoveryDate > endDate) return;
        }

        const amount = recovery.recoveryAmount || 0;
        allTransactions.push({
          _id: recovery._id,
          date: recovery.date,
          type: 'deposit',
          amount: amount,
          description: `Recovery #${recovery.serialNumber} - ${recovery.customerNameUrdu || recovery.customerName}`,
          descriptionUrdu: `ریکوری #${recovery.serialNumber} - ${recovery.customerNameUrdu || recovery.customerName}`,
          recoveryId: recovery._id,
          createdAt: new Date(recovery.createdAt || recovery.date)
        });
      });

      // Add withdrawals (expenses)
      expenses.forEach(expense => {
        // Filter by date range if provided
        const expenseDate = new Date(expense.date);
        if (dateRange.startDate && expenseDate < new Date(dateRange.startDate)) return;
        if (dateRange.endDate) {
          const endDate = new Date(dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (expenseDate > endDate) return;
        }

        const amount = (expense.travellingExpense || 0) + (expense.refreshment || 0) +
          (expense.cargo || 0) + (expense.salaryExpense || 0);
        allTransactions.push({
          _id: expense._id,
          date: expense.date,
          type: 'withdraw',
          amount: amount,
          description: `Expense - ${expense.descriptionUrdu || expense.description || 'اخراجات'}`,
          descriptionUrdu: `اخراجات - ${expense.descriptionUrdu || expense.description || 'Expense'}`,
          expenseId: expense._id,
          createdAt: new Date(expense.createdAt || expense.date)
        });
      });

      // Add bank transactions (inter-bank transfers, direct deposits/withdrawals)
      bankTransactions.forEach(tx => {
        // Filter by date range if provided
        const txDate = new Date(tx.date);
        if (dateRange.startDate && txDate < new Date(dateRange.startDate)) return;
        if (dateRange.endDate) {
          const endDate = new Date(dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (txDate > endDate) return;
        }

        const amount = tx.amount || 0;
        const isTransfer = tx.description && (tx.description.includes('Transfer') || tx.description.includes('ٹرانسفر') || tx.descriptionUrdu?.includes('ٹرانسفر'));

        // Extract bank name from description if it's a transfer
        let transferBankName = '';
        if (isTransfer && tx.descriptionUrdu) {
          // Try to extract bank name from Urdu description
          // Format: "بینک ٹرانسفر - [Bank Name]" or "بینک ٹرانسفر - [Bank Name] سے" or "بینک ٹرانسفر - [Bank Name] میں"
          const match = tx.descriptionUrdu.match(/ٹرانسفر\s*-\s*([^\s]+(?:\s+[^\s]+)*?)(?:\s+(?:سے|میں))?$/);
          if (match) {
            transferBankName = match[1].trim();
          }
        }
        if (!transferBankName && isTransfer && tx.description) {
          // Try to extract from English description
          // Format: "Bank Transfer - [Bank Name]" or "Bank Transfer - from [Bank Name]" or "Bank Transfer - to [Bank Name]"
          const match = tx.description.match(/Transfer\s*-\s*(?:from|to)?\s*([^-]+?)(?:\s*$)/i);
          if (match) {
            transferBankName = match[1].trim();
          }
        }

        // Format description based on transaction type
        let displayDescription = tx.description || (tx.type === 'deposit' ? 'Deposit' : 'Withdrawal');
        let displayDescriptionUrdu = tx.descriptionUrdu || tx.description || '';

        if (isTransfer) {
          if (transferBankName) {
            // Bank name found in description
            if (tx.type === 'deposit') {
              // For deposit: show source bank (where money came from)
              displayDescriptionUrdu = `بینک ٹرانسفر - ${transferBankName} سے`;
              displayDescription = `Bank Transfer - from ${transferBankName}`;
            } else {
              // For withdrawal: show destination bank (where money is going)
              displayDescriptionUrdu = `بینک ٹرانسفر - ${transferBankName} میں`;
              displayDescription = `Bank Transfer - to ${transferBankName}`;
            }
          } else {
            // Transfer detected but no bank name - try to use original description
            if (tx.descriptionUrdu && tx.descriptionUrdu.includes('ٹرانسفر')) {
              displayDescriptionUrdu = tx.descriptionUrdu;
            } else if (tx.description && tx.description.includes('Transfer')) {
              displayDescriptionUrdu = tx.description;
            } else {
              displayDescriptionUrdu = 'بینک ٹرانسفر';
              displayDescription = 'Bank Transfer';
            }
          }
        } else {
          // Check if transaction is from SettledBill (Roznamacha)
          const isRoznamacha = (tx.description && (tx.description.includes('Roznamacha') || tx.description.includes('روزنامچہ'))) ||
            (tx.descriptionUrdu && tx.descriptionUrdu.includes('روزنامچہ'));

          if (isRoznamacha) {
            console.log('Roznamacha transaction found:', {
              description: tx.description,
              descriptionUrdu: tx.descriptionUrdu,
              date: tx.date,
              amount: tx.amount,
              billsCount: bills.length,
              ledgerPartiesCount: ledgerParties.length
            });

            // First, try to extract bill number directly from description if it's included
            // Format from SettledBill: "روزنامچہ - [BillNumber] - [Party] - رقم: [Amount]"
            // Or: "روزنامچہ - [Party] - رقم: [Amount]" (if no bill number)
            let billNumber = null;

            // Split description by dashes to get parts
            const descParts = (tx.descriptionUrdu || tx.description || '').split(/[-–]/).map(p => p.trim());

            // Format: "روزنامچہ - [BillNumber] - [Party] - رقم: [Amount]"
            // So parts would be: ["روزنامچہ", "[BillNumber]", "[Party]", "رقم: [Amount]"]
            if (descParts.length >= 3 && (descParts[0].includes('روزنامچہ') || descParts[0].includes('Roznamacha'))) {
              // Check if second part (index 1) is a bill number
              const potentialBillNumber = descParts[1];

              // Check if it's not an ObjectId (24 hex chars) and contains digits
              if (potentialBillNumber &&
                !/^[0-9a-fA-F]{24}$/.test(potentialBillNumber) &&
                /\d/.test(potentialBillNumber) &&
                potentialBillNumber.length < 50) { // Bill numbers are usually short
                billNumber = potentialBillNumber;
              }
            }

            // Also try regex pattern as fallback
            if (!billNumber) {
              const billNumberMatch = tx.descriptionUrdu?.match(/روزنامچہ\s*[-–]\s*([^-–]+?)\s*[-–]\s*[^-–]+?\s*[-–]\s*رقم/) ||
                tx.description?.match(/Roznamacha\s*[-–]\s*([^-–]+?)\s*[-–]\s*[^-–]+?\s*[-–]\s*Amount/);

              if (billNumberMatch && billNumberMatch[1]) {
                const extracted = billNumberMatch[1].trim();
                if (extracted && !/^[0-9a-fA-F]{24}$/.test(extracted) && /\d/.test(extracted)) {
                  billNumber = extracted;
                }
              }
            }

            // Try simpler pattern: anything between first and second dash
            if (!billNumber) {
              const simpleMatch = tx.descriptionUrdu?.match(/روزنامچہ\s*[-–]\s*([^-–]+?)\s*[-–]/) ||
                tx.description?.match(/Roznamacha\s*[-–]\s*([^-–]+?)\s*[-–]/);
              if (simpleMatch && simpleMatch[1]) {
                const extracted = simpleMatch[1].trim();
                if (extracted && !/^[0-9a-fA-F]{24}$/.test(extracted) && /\d/.test(extracted) && extracted.length < 50) {
                  billNumber = extracted;
                }
              }
            }

            if (billNumber) {
              // Bill number found directly in description
              console.log('Found bill number in description:', billNumber, 'from:', tx.descriptionUrdu || tx.description);
              displayDescriptionUrdu = `بل ${billNumber}`;
              displayDescription = `Bill ${billNumber}`;
            } else {
              // Extract party name from description
              const partyNameMatch = tx.descriptionUrdu?.match(/روزنامچہ\s*[-–]\s*([^-–]+?)\s*[-–]\s*رقم/) ||
                tx.description?.match(/Roznamacha\s*[-–]\s*([^-–]+?)\s*[-–]\s*Amount/);

              if (partyNameMatch && partyNameMatch[1]) {
                const partyName = partyNameMatch[1].trim();
                console.log('Extracted party name:', partyName);

                // Check if partyName is a party ID (ObjectId format)
                const isPartyId = /^[0-9a-fA-F]{24}$/.test(partyName);

                // Extract amount from description for better matching
                const amountMatch = tx.descriptionUrdu?.match(/رقم:\s*([\d,]+\.?\d*)/) ||
                  tx.description?.match(/Amount:\s*([\d,]+\.?\d*)/);
                const txAmount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : tx.amount;

                // Try to find matching bill by party name/ID, date, and amount
                let matchingBill = null;

                // First, try to find bills matching the party
                const candidateBills = bills.filter(bill => {
                  const billPartyId = bill.customerPartyId?.toString();
                  const billCustomerName = bill.customerName || '';
                  const billCustomerNameUrdu = bill.customerNameUrdu || '';

                  // Match by party ID
                  if (isPartyId && partyName === billPartyId) {
                    return true;
                  }

                  // Match by party name
                  if (!isPartyId) {
                    if (partyName === billCustomerName || partyName === billCustomerNameUrdu) {
                      return true;
                    }

                    // Match by ledger party
                    const ledgerParty = ledgerParties.find(p => {
                      const pId = p._id?.toString();
                      const pName = p.name || '';
                      const pNameUrdu = p.nameUrdu || '';
                      return (partyName === pName || partyName === pNameUrdu) &&
                        (pId === billPartyId);
                    });

                    if (ledgerParty) return true;
                  } else {
                    // Match by ledger party ID
                    const ledgerParty = ledgerParties.find(p => {
                      const pId = p._id?.toString();
                      return partyName === pId && pId === billPartyId;
                    });

                    if (ledgerParty) return true;
                  }

                  return false;
                });

                console.log(`Found ${candidateBills.length} candidate bills for party: ${partyName}`);

                // Now filter by date and amount
                if (candidateBills.length > 0) {
                  const txDate = new Date(tx.date).toISOString().split('T')[0];

                  // Try exact date match first
                  matchingBill = candidateBills.find(bill => {
                    const billDate = new Date(bill.date).toISOString().split('T')[0];
                    return billDate === txDate;
                  });

                  // If no exact date match, try within 30 days (more flexible)
                  if (!matchingBill) {
                    matchingBill = candidateBills.find(bill => {
                      const billDate = new Date(bill.date).toISOString().split('T')[0];
                      const dateDiff = Math.abs(new Date(billDate).getTime() - new Date(txDate).getTime());
                      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
                      return dateDiff <= thirtyDaysMs;
                    });
                  }

                  // If still no match and we have amount, try matching by amount
                  if (!matchingBill && txAmount !== null) {
                    matchingBill = candidateBills.find(bill => {
                      // Calculate bill's net sale or total amount
                      const billNetSale = bill.netSale || 0;
                      const billSale = bill.sale || 0;
                      const billTotalSale = bill.totalSale || 0;
                      const billGrossSale = bill.grossSale || 0;

                      // Check if transaction amount matches any bill amount (within 5% tolerance)
                      const tolerance = 0.05;
                      const amounts = [billNetSale, billSale, billTotalSale, billGrossSale];
                      return amounts.some(billAmt => {
                        if (billAmt === 0 && txAmount === 0) return true;
                        if (billAmt === 0 || txAmount === 0) return false;
                        return Math.abs(billAmt - txAmount) / Math.max(billAmt, txAmount) <= tolerance;
                      });
                    });
                  }

                  // If still no match, just take the first candidate bill (most recent)
                  if (!matchingBill && candidateBills.length > 0) {
                    // Sort by date descending and take the first one
                    const sortedBills = [...candidateBills].sort((a, b) => {
                      return new Date(b.date) - new Date(a.date);
                    });
                    matchingBill = sortedBills[0];
                  }
                }

                if (matchingBill) {
                  // Show only bill number
                  const billNumber = matchingBill.billNumber || matchingBill.billNo || matchingBill.serialNumber || matchingBill._id?.toString().slice(-6) || '';
                  console.log('Found matching bill:', {
                    billId: matchingBill._id,
                    billNumber: billNumber,
                    billDate: matchingBill.date,
                    partyId: matchingBill.customerPartyId
                  });
                  displayDescriptionUrdu = `بل ${billNumber}`;
                  displayDescription = `Bill ${billNumber}`;
                } else {
                  console.log('No matching bill found for transaction:', {
                    partyName: partyName,
                    txDate: tx.date,
                    txAmount: txAmount,
                    candidateBillsCount: candidateBills.length
                  });
                  // Bill not found, show only "روزنامچہ"
                  displayDescriptionUrdu = 'روزنامچہ';
                  displayDescription = 'Roznamacha';
                }
              } else {
                console.log('Could not extract party name from description:', {
                  description: tx.description,
                  descriptionUrdu: tx.descriptionUrdu
                });
                // Could not extract party name, show only "روزنامچہ"
                displayDescriptionUrdu = 'روزنامچہ';
                displayDescription = 'Roznamacha';
              }
            }
          } else {
            // Not a transfer or roznamacha - use original description
            displayDescriptionUrdu = displayDescriptionUrdu || (tx.type === 'deposit' ? 'جمع' : 'نکال');
          }
        }

        allTransactions.push({
          _id: tx._id,
          date: tx.date,
          type: tx.type === 'deposit' ? 'deposit' : 'withdraw',
          amount: amount,
          description: displayDescription,
          descriptionUrdu: displayDescriptionUrdu,
          originalDescription: tx.description, // Keep original for name extraction
          originalDescriptionUrdu: tx.descriptionUrdu, // Keep original for name extraction
          bankTransactionId: tx._id,
          isTransfer: isTransfer,
          transferBankName: transferBankName,
          createdAt: new Date(tx.createdAt || tx.date)
        });
      });

      // Sort by date and createdAt
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        return a.createdAt - b.createdAt;
      });

      // Calculate running balance
      let runningBalance = selectedBank.openingBalance || 0;
      const transactions = allTransactions.map(tx => {
        if (tx.type === 'deposit') {
          runningBalance += tx.amount;
        } else {
          runningBalance -= tx.amount;
        }
        return {
          ...tx,
          balanceAfter: runningBalance
        };
      });

      setTransactions(transactions);
    } catch (error) {
      console.error("Fetch transactions error:", error);
      toast.error(companyLanguage === 'ur' ? 'لین دین لوڈ کرنے میں ناکام' : "Failed to load transactions");
    }
  };

  const calculateCurrentBalance = () => {
    if (!selectedBank) return 0;
    // Use cached balance from bankBalances if available (includes all transactions)
    if (bankBalances[selectedBank._id] !== undefined) {
      return bankBalances[selectedBank._id];
    }
    // Fallback: calculate from transactions if balance not cached yet
    let balance = selectedBank.openingBalance || 0;
    transactions.forEach(tx => {
      if (tx.type === 'deposit') {
        balance += tx.amount;
      } else {
        balance -= tx.amount;
      }
    });
    return balance;
  };

  const fetchAllBankBalances = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token || paymentMethods.length === 0) return;

      // Get all recoveries for this company
      const allRecoveriesResponse = await api.get(`/recovery/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get all expenses for this company
      const allExpensesResponse = await api.get(`/expense-ledger/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recoveries = allRecoveriesResponse.data.recoveries || [];
      const expenses = allExpensesResponse.data.expenses || [];

      // Get all bank transactions (including transfers)
      let bankTransactions = [];
      try {
        const allBankTransactionsResponse = await api.get(`/bank/company/transactions/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        bankTransactions = allBankTransactionsResponse.data.transactions || [];
      } catch (txError) {
        console.error("Error fetching bank transactions:", txError);
        // Continue without bank transactions if endpoint doesn't exist
      }

      // Calculate balance for each bank
      const balances = {};

      paymentMethods.forEach(bank => {
        const bankName = bank.bankName || bank.name;
        const bankNameUrdu = bank.bankNameUrdu || bank.nameUrdu;

        let balance = bank.openingBalance || 0;

        // Add deposits (recoveries)
        recoveries.forEach(recovery => {
          const paymentMethod = recovery.paymentMethod || '';
          if (paymentMethod === bankName ||
            paymentMethod === bankNameUrdu ||
            paymentMethod === bank.bankName ||
            paymentMethod === bank.bankNameUrdu) {
            balance += recovery.recoveryAmount || 0;
          }
        });

        // Subtract withdrawals (expenses)
        expenses.forEach(expense => {
          const paymentMethod = expense.paymentMethod || '';
          if (paymentMethod === bankName ||
            paymentMethod === bankNameUrdu ||
            paymentMethod === bank.bankName ||
            paymentMethod === bank.bankNameUrdu) {
            const expenseAmount = (expense.travellingExpense || 0) +
              (expense.refreshment || 0) +
              (expense.cargo || 0) +
              (expense.salaryExpense || 0);
            balance -= expenseAmount;
          }
        });

        // Add/subtract from bank transactions (including transfers)
        bankTransactions.forEach(tx => {
          const paymentMethod = tx.paymentMethod || '';
          if (paymentMethod === bankName ||
            paymentMethod === bankNameUrdu ||
            paymentMethod === bank.bankName ||
            paymentMethod === bank.bankNameUrdu) {
            if (tx.type === 'deposit') {
              balance += (tx.amount || 0);
            } else if (tx.type === 'withdraw') {
              balance -= (tx.amount || 0);
            }
          }
        });

        balances[bank._id] = balance;
      });

      setBankBalances(balances);
      console.log('Updated bank balances:', balances);
    } catch (error) {
      console.error("Fetch bank balances error:", error);
    }
  };

  const calculateBankBalance = (bankId) => {
    // Return cached balance if available, otherwise return opening balance
    if (bankBalances[bankId] !== undefined) {
      return bankBalances[bankId];
    }

    const bank = paymentMethods.find(pm => pm._id === bankId);
    return bank?.openingBalance || 0;
  };


  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    const isUrdu = companyLanguage === 'ur';

    if (!transactionForm.fromBank) {
      toast.error(isUrdu ? 'سورس بینک منتخب کریں' : "Please select source bank");
      return;
    }

    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      toast.error(isUrdu ? 'درست رقم درج کریں' : "Please enter a valid amount");
      return;
    }

    const amount = parseFloat(transactionForm.amount);
    const sourceBankBalance = calculateBankBalance(transactionForm.fromBank);

    // Check if source bank has sufficient balance
    if (amount > sourceBankBalance) {
      toast.error(isUrdu ? 'سورس بینک میں ناکافی بیلنس ہے' : "Insufficient balance in source bank");
      return;
    }

    if (transactionForm.type === 'deposit' && !transactionForm.toBank) {
      toast.error(isUrdu ? 'منزل بینک منتخب کریں' : "Please select destination bank");
      return;
    }

    if (transactionForm.type === 'deposit' && transactionForm.fromBank === transactionForm.toBank) {
      toast.error(isUrdu ? 'سورس اور منزل بینک مختلف ہونے چاہئیں' : "Source and destination banks must be different");
      return;
    }

    try {
      const token = localStorage.getItem("companyToken");

      const sourceBank = paymentMethods.find(pm => pm._id === transactionForm.fromBank);
      if (!sourceBank) {
        toast.error(isUrdu ? 'سورس بینک نہیں ملا' : "Source bank not found");
        return;
      }

      // Get bank names for transfer description
      let destBankName = '';
      let sourceBankName = '';

      // Get source bank name
      if (sourceBank) {
        sourceBankName = isUrdu ? (sourceBank.nameUrdu || sourceBank.name) : sourceBank.name;
      }

      // Get destination bank name
      if (transactionForm.type === 'deposit' && transactionForm.toBank) {
        const destBank = paymentMethods.find(pm => pm._id === transactionForm.toBank);
        if (destBank) {
          destBankName = isUrdu ? (destBank.nameUrdu || destBank.name) : destBank.name;
        }
      }

      // Withdraw from source bank (using payment method)
      // For withdrawal: show destination bank (where money is going)
      const withdrawPayload = {
        paymentMethodId: transactionForm.fromBank,
        date: transactionForm.date,
        type: 'withdraw',
        amount: amount,
        description: transactionForm.description || (transactionForm.type === 'deposit' && destBankName
          ? (isUrdu ? `بینک ٹرانسفر - ${destBankName}` : `Bank Transfer - ${destBankName}`)
          : (isUrdu ? 'بینک ٹرانسفر' : 'Bank Transfer')),
        descriptionUrdu: transactionForm.descriptionUrdu || (transactionForm.type === 'deposit' && destBankName
          ? `بینک ٹرانسفر - ${destBankName}`
          : 'بینک ٹرانسفر')
      };

      console.log('Withdrawing from source bank:', withdrawPayload);
      const withdrawResponse = await api.post(`/bank/company/payment-method/transaction`, withdrawPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Withdraw response:', withdrawResponse.data);

      // If deposit, also deposit to destination bank
      // For deposit: show source bank (where money came from)
      if (transactionForm.type === 'deposit' && transactionForm.toBank) {
        const destBank = paymentMethods.find(pm => pm._id === transactionForm.toBank);
        if (!destBank) {
          toast.error(isUrdu ? 'منزل بینک نہیں ملا' : "Destination bank not found");
          return;
        }

        const depositPayload = {
          paymentMethodId: transactionForm.toBank,
          date: transactionForm.date,
          type: 'deposit',
          amount: amount,
          description: transactionForm.description || (isUrdu ? `بینک ٹرانسفر - ${sourceBankName}` : `Bank Transfer - ${sourceBankName}`),
          descriptionUrdu: transactionForm.descriptionUrdu || `بینک ٹرانسفر - ${sourceBankName}`
        };

        console.log('Depositing to destination bank:', depositPayload);
        const depositResponse = await api.post(`/bank/company/payment-method/transaction`, depositPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Deposit response:', depositResponse.data);
      }

      toast.success(isUrdu ? 'ٹرانسفر کامیاب' : "Transfer successful");

      // Immediately update balances in state (optimistic update)
      if (transactionForm.fromBank) {
        const currentFromBalance = calculateBankBalance(transactionForm.fromBank);
        const newFromBalance = currentFromBalance - amount;
        setBankBalances(prev => ({
          ...prev,
          [transactionForm.fromBank]: newFromBalance
        }));
      }

      if (transactionForm.type === 'deposit' && transactionForm.toBank) {
        const currentToBalance = calculateBankBalance(transactionForm.toBank);
        const newToBalance = currentToBalance + amount;
        setBankBalances(prev => ({
          ...prev,
          [transactionForm.toBank]: newToBalance
        }));
      }

      setShowAddTransactionModal(false);
      setEditingTransaction(null);
      resetTransactionForm();

      // Refresh transactions for both banks
      if (selectedBank) {
        fetchBankTransactions(selectedBank._id);
      }

      // Refresh payment methods first
      await fetchPaymentMethods();

      // Then refresh balances in background (will sync with server)
      await fetchAllBankBalances();
    } catch (error) {
      console.error("Transfer error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Full error:", JSON.stringify(error.response?.data, null, 2));
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      toast.error(isUrdu ? `ٹرانسفر میں ناکام: ${errorMessage}` : `Transfer failed: ${errorMessage}`);
    }
  };


  const handleAddBalance = async (e) => {
    e.preventDefault();
    if (!selectedBank) {
      toast.error(isUrdu ? 'براہ کرم بینک منتخب کریں' : 'Please select a bank');
      return;
    }

    const amount = parseFloat(addBalanceForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(isUrdu ? 'براہ کرم درست رقم درج کریں' : 'Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem("companyToken");

      // Create deposit transaction using payment method API
      const payload = {
        paymentMethodId: selectedBank.paymentMethodId || selectedBank._id,
        date: addBalanceForm.date,
        type: 'deposit',
        amount: amount,
        description: addBalanceForm.description || (isUrdu ? 'بیلنس شامل کیا گیا' : 'Balance Added'),
        descriptionUrdu: addBalanceForm.descriptionUrdu || 'بیلنس شامل کیا گیا'
      };

      await api.post(`/bank/company/payment-method/transaction`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(isUrdu ? 'بیلنس کامیابی سے شامل ہو گیا' : 'Balance added successfully');

      // Update balance in state
      const currentBalance = calculateBankBalance(selectedBank._id);
      const newBalance = currentBalance + amount;
      setBankBalances(prev => ({
        ...prev,
        [selectedBank._id]: newBalance
      }));

      setShowAddBalanceModal(false);
      setAddBalanceForm({
        date: new Date().toLocaleDateString('en-CA'),
        amount: '',
        description: '',
        descriptionUrdu: ''
      });

      // Refresh transactions
      if (selectedBank) {
        fetchBankTransactions(selectedBank._id);
      }
      fetchAllBankBalances();
    } catch (error) {
      console.error("Add balance error:", error);
      toast.error(isUrdu ? 'بیلنس شامل کرنے میں ناکام' : 'Failed to add balance');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی یہ لین دین حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this transaction?')) {
      return;
    }
    try {
      const token = localStorage.getItem("companyToken");
      await api.delete(`/bank/company/transaction/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'لین دین حذف ہو گیا' : "Transaction deleted successfully");
      fetchBankTransactions(selectedBank._id);
      // Remove from selected transactions if it was selected
      setSelectedTransactions(prev => prev.filter(id => id !== transactionId));
    } catch (error) {
      console.error("Delete transaction error:", error);
      toast.error(companyLanguage === 'ur' ? 'لین دین حذف کرنے میں ناکام' : "Failed to delete transaction");
    }
  };

  // Handle select/deselect individual transaction
  const handleToggleTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  // Handle select all transactions
  const handleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      // Deselect all
      setSelectedTransactions([]);
    } else {
      // Select all
      setSelectedTransactions(transactions.map(tx => tx._id));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedTransactions.length === 0) {
      toast.error(companyLanguage === 'ur' ? 'براہ کرم کم از کم ایک لین دین منتخب کریں' : 'Please select at least one transaction');
      return;
    }

    const confirmMessage = companyLanguage === 'ur'
      ? `کیا آپ واقعی ${selectedTransactions.length} لین دین حذف کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedTransactions.length} transaction(s)?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem("companyToken");
      const deletePromises = selectedTransactions.map(transactionId =>
        api.delete(`/bank/company/transaction/${transactionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);
      toast.success(
        companyLanguage === 'ur'
          ? `${selectedTransactions.length} لین دین حذف ہو گئے`
          : `${selectedTransactions.length} transaction(s) deleted successfully`
      );
      setSelectedTransactions([]);
      fetchBankTransactions(selectedBank._id);
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error(companyLanguage === 'ur' ? 'لین دین حذف کرنے میں ناکام' : "Failed to delete transactions");
    } finally {
      setIsDeleting(false);
    }
  };


  const resetTransactionForm = () => {
    setTransactionForm({
      date: new Date().toLocaleDateString('en-CA'),
      type: 'deposit',
      amount: '',
      fromBank: '',
      toBank: '',
      paymentMethod: '',
      description: '',
      descriptionUrdu: ''
    });
  };

  // Inter-Bank Transaction Functions
  const handleAddInterBankTransaction = async (e) => {
    e.preventDefault();
    try {
      if (!interBankForm.fromBank || !interBankForm.toBank || !interBankForm.amount) {
        toast.error(isUrdu ? 'تمام فیلڈز بھریں' : 'Please fill all fields');
        return;
      }

      if (interBankForm.fromBank === interBankForm.toBank) {
        toast.error(isUrdu ? 'منبع اور منزل بینک مختلف ہونے چاہئیں' : 'From and To banks must be different');
        return;
      }

      const amount = Number(interBankForm.amount);
      if (amount <= 0) {
        toast.error(isUrdu ? 'رقم صفر سے زیادہ ہونی چاہیے' : 'Amount must be greater than zero');
        return;
      }

      const newTransaction = {
        _id: Date.now().toString(),
        date: interBankForm.date,
        fromBank: interBankForm.fromBank,
        toBank: interBankForm.toBank,
        amount: amount,
        description: interBankForm.description,
        descriptionUrdu: interBankForm.descriptionUrdu,
        createdAt: new Date()
      };

      setInterBankTransactions([...interBankTransactions, newTransaction]);
      setInterBankForm({
        date: new Date().toLocaleDateString('en-CA'),
        fromBank: '',
        toBank: '',
        amount: '',
        description: '',
        descriptionUrdu: ''
      });
      setShowInterBankModal(false);
      toast.success(isUrdu ? 'بینک لین دین شامل ہو گیا' : 'Inter-bank transaction added');
    } catch (error) {
      console.error('Add inter-bank transaction error:', error);
      toast.error(isUrdu ? 'لین دین شامل کرنے میں ناکام' : 'Failed to add transaction');
    }
  };

  const handleDeleteInterBankTransaction = (transactionId) => {
    setInterBankTransactions(interBankTransactions.filter(tx => tx._id !== transactionId));
    toast.success(isUrdu ? 'لین دین حذف ہو گیا' : 'Transaction deleted');
  };

  const fetchBankHistory = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Fetch all bank transactions
      const allBankTransactionsResponse = await api.get(`/bank/company/transactions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allTransactions = allBankTransactionsResponse.data.transactions || [];

      // Sort by date (newest first)
      allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

      setBankHistoryTransactions(allTransactions);
    } catch (error) {
      console.error("Fetch bank history error:", error);
      toast.error(companyLanguage === 'ur' ? 'بینک کی تاریخ لوڈ کرنے میں ناکام' : "Failed to load bank history");
    }
  };

  const handleExportInterBankExcel = () => {
    try {
      // Prepare data for Excel - withdrawals on left, deposits on right
      const excelData = [];

      // Title row
      excelData.push({
        [isUrdu ? 'نکال (Withdrawals)' : 'Withdrawals']: '',
        '': '',
        '': '',
        '': '',
        '': '', // Empty column separator
        [isUrdu ? 'جمع (Deposits)' : 'Deposits']: '',
        '': '',
        '': '',
        '': ''
      });

      // Header row
      excelData.push({
        [isUrdu ? 'تاریخ' : 'Date']: '',
        [isUrdu ? 'بینک' : 'Bank']: '',
        [isUrdu ? 'رقم' : 'Amount']: '',
        [isUrdu ? 'تفصیل' : 'Description']: '',
        '': '', // Empty column separator
        [isUrdu ? 'تاریخ' : 'Date']: '',
        [isUrdu ? 'بینک' : 'Bank']: '',
        [isUrdu ? 'رقم' : 'Amount']: '',
        [isUrdu ? 'تفصیل' : 'Description']: ''
      });

      // Get all unique dates
      const allDates = [...new Set(interBankTransactions.map(tx => tx.date))].sort();

      // Separate withdrawals and deposits
      const withdrawals = [];
      const deposits = [];

      interBankTransactions.forEach(tx => {
        const fromBankName = paymentMethods.find(pm => pm._id === tx.fromBank)?.nameUrdu ||
          paymentMethods.find(pm => pm._id === tx.fromBank)?.name ||
          'Unknown';
        const toBankName = paymentMethods.find(pm => pm._id === tx.toBank)?.nameUrdu ||
          paymentMethods.find(pm => pm._id === tx.toBank)?.name ||
          'Unknown';

        withdrawals.push({
          date: tx.date,
          bank: fromBankName,
          amount: tx.amount,
          description: tx.descriptionUrdu || tx.description || ''
        });

        deposits.push({
          date: tx.date,
          bank: toBankName,
          amount: tx.amount,
          description: tx.descriptionUrdu || tx.description || ''
        });
      });

      // Sort by date
      withdrawals.sort((a, b) => new Date(a.date) - new Date(b.date));
      deposits.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Get max rows needed
      const maxRows = Math.max(withdrawals.length, deposits.length);

      // Create rows with withdrawals on left and deposits on right
      for (let i = 0; i < maxRows; i++) {
        const withdraw = withdrawals[i];
        const deposit = deposits[i];

        const row = {
          [isUrdu ? 'تاریخ' : 'Date']: withdraw ? withdraw.date : '',
          [isUrdu ? 'بینک' : 'Bank']: withdraw ? withdraw.bank : '',
          [isUrdu ? 'رقم' : 'Amount']: withdraw ? withdraw.amount : '',
          [isUrdu ? 'تفصیل' : 'Description']: withdraw ? withdraw.description : '',
          '': '', // Separator
          [isUrdu ? 'تاریخ' : 'Date']: deposit ? deposit.date : '',
          [isUrdu ? 'بینک' : 'Bank']: deposit ? deposit.bank : '',
          [isUrdu ? 'رقم' : 'Amount']: deposit ? deposit.amount : '',
          [isUrdu ? 'تفصیل' : 'Description']: deposit ? deposit.description : ''
        };
        excelData.push(row);
      }

      // Add totals row
      const totalWithdraw = withdrawals.reduce((sum, tx) => sum + (tx.amount || 0), 0);
      const totalDeposit = deposits.reduce((sum, tx) => sum + (tx.amount || 0), 0);

      excelData.push({
        [isUrdu ? 'تاریخ' : 'Date']: '',
        [isUrdu ? 'بینک' : 'Bank']: isUrdu ? 'کل' : 'Total',
        [isUrdu ? 'رقم' : 'Amount']: totalWithdraw,
        [isUrdu ? 'تفصیل' : 'Description']: '',
        '': '',
        [isUrdu ? 'تاریخ' : 'Date']: '',
        [isUrdu ? 'بینک' : 'Bank']: isUrdu ? 'کل' : 'Total',
        [isUrdu ? 'رقم' : 'Amount']: totalDeposit,
        [isUrdu ? 'تفصیل' : 'Description']: ''
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isUrdu ? 'بینک لین دین' : 'Inter-Bank Transactions');

      const filename = `Inter-Bank-Transactions-${new Date().toLocaleDateString('en-CA')}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : "Excel file downloaded successfully");
    } catch (error) {
      console.error("Export Excel error:", error);
      toast.error(isUrdu ? 'Excel فائل بنانے میں ناکام' : "Failed to export Excel file");
    }
  };

  // Download PDF for Bank Transactions
  const handleDownloadPDF = async () => {
    if (!printRef.current || !selectedBank) {
      toast.error(isUrdu ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(isUrdu ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print header and hide other elements for PDF
      const printHeader = element.querySelector('.pdf-print-header');

      // Show print header
      if (printHeader) {
        printHeader.style.display = 'block';
      }

      // Hide elements that should be hidden in print
      const hiddenElements = element.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .bg-gradient-to-br { background: white !important; }
        .bg-brand-background { background: white !important; }
        .bg-white { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0.5cm !important; }
        .rounded-xl, .rounded-lg { border-radius: 0 !important; }
        .shadow-lg, .shadow-2xl { box-shadow: none !important; }
        table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
        .overflow-x-auto { width: 100% !important; overflow: visible !important; }
        div[ref] { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original display states
      if (printHeader) {
        printHeader.style.display = '';
      }
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const bankName = selectedBank.bankNameUrdu || selectedBank.bankName || 'Bank';
      const fileName = `${bankName}_Transactions_${new Date().toLocaleDateString('en-CA')}.pdf`;

      pdf.save(fileName);
      toast.success(isUrdu ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(isUrdu ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download Excel for Bank Transactions
  const handleDownloadExcel = () => {
    if (!selectedBank || transactions.length === 0) {
      toast.error(isUrdu ? 'کوئی لین دین نہیں' : 'No transactions to export');
      return;
    }

    try {
      const excelData = [];

      // Add header
      excelData.push([
        isUrdu ? 'تاریخ' : 'Date',
        isUrdu ? 'نام' : 'Name',
        isUrdu ? 'تفصیل' : 'Description',
        isUrdu ? 'قسم' : 'Type',
        isUrdu ? 'رقم' : 'Amount',
        isUrdu ? 'بیلنس' : 'Balance'
      ]);

      // Add data rows
      transactions.forEach(tx => {
        const originalDesc = tx.originalDescription || tx.description;
        const originalDescUrdu = tx.originalDescriptionUrdu || tx.descriptionUrdu;
        const extractedName = extractNameFromDescription(originalDesc, originalDescUrdu);
        const description = isUrdu ? (tx.descriptionUrdu || tx.description || '-') : (tx.description || tx.descriptionUrdu || '-');
        const type = tx.type === 'deposit' ? (isUrdu ? 'جمع' : 'Deposit') : (isUrdu ? 'نکال' : 'Withdraw');

        excelData.push([
          new Date(tx.date).toLocaleDateString('en-GB'),
          extractedName,
          description,
          type,
          tx.amount.toLocaleString(),
          (tx.balanceAfter || 0).toLocaleString()
        ]);
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      const bankName = selectedBank.bankNameUrdu || selectedBank.bankName || 'Bank';
      XLSX.utils.book_append_sheet(wb, ws, bankName.substring(0, 31)); // Sheet name max 31 chars

      // Generate filename
      const fileName = `${bankName}_Transactions_${new Date().toLocaleDateString('en-CA')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(isUrdu ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  const isUrdu = companyLanguage === 'ur';
  const currentBalance = calculateCurrentBalance();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
            {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'ادائیگی کھاتا' : 'Payment Methods Ledger'}</title>
      </Helmet>
      <div className={`min-h-screen bg-brand-background pt-[6px] pb-6 px-4 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-3 mb-3 border border-brand-card animate-fade-in-up">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className={`text-xl font-bold text-brand-header mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'ادائیگی کھاتا' : 'Payment Methods Ledger'}
                </h1>
                <p className={`text-gray-600 text-sm ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'ادائیگی کے طریقے اور لین دین کا انتظام' : 'Manage payment methods and transactions'}
                </p>
              </div>
            </div>

            {/* Selected bank summary moved into header (ادائیگی کھاتا والا باکس) */}
            {selectedBank ? (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-2 print:hidden">
                <div className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-bold text-brand-header truncate ${isUrdu ? 'font-urdu' : ''}`}>
                      {selectedBank.bankNameUrdu || selectedBank.bankName}
                    </div>
                    <div className="mt-1 space-y-0.5 text-[11px] text-gray-700">
                      {selectedBank.accountNumber && (
                        <div className="truncate">
                          <span className="font-semibold text-brand-header">{isUrdu ? 'اکاؤنٹ:' : 'A/C:'}</span> {selectedBank.accountNumber}
                        </div>
                      )}
                      {selectedBank.accountTitle && (
                        <div className="truncate">
                          <span className="font-semibold text-brand-header">{isUrdu ? 'ٹائٹل:' : 'Title:'}</span> {selectedBank.accountTitle}
                        </div>
                      )}
                      {selectedBank.branchName && (
                        <div className="truncate">
                          <span className="font-semibold text-brand-header">{isUrdu ? 'برانچ:' : 'Branch:'}</span> {selectedBank.branchName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`flex items-center gap-2 flex-wrap ${isUrdu ? 'justify-start' : 'justify-end'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setAddBalanceForm({
                          date: new Date().toLocaleDateString('en-CA'),
                          amount: '',
                          description: '',
                          descriptionUrdu: ''
                        });
                        setShowAddBalanceModal(true);
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-2"
                    >
                      <span>{isUrdu ? 'بیلنس شامل کریں' : 'Add Balance'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        resetTransactionForm();
                        setEditingTransaction(null);
                        setShowAddTransactionModal(true);
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900 text-white text-xs font-semibold rounded-lg transition-all"
                    >
                      {isUrdu ? 'بینک ٹرانسفر' : 'Bank Transfer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGeneralVoucherForm((prev) => ({
                          ...prev,
                          fromBank: selectedBank?._id || prev.fromBank,
                          date: new Date().toLocaleDateString('en-CA')
                        }));
                        setShowGeneralVoucherModal(true);
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white text-xs font-semibold rounded-lg transition-all"
                    >
                      {isUrdu ? 'جنرل واؤچر' : 'General Voucher'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBankHistory(true);
                        fetchBankHistory();
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white text-xs font-semibold rounded-lg transition-all"
                    >
                      {isUrdu ? 'بینک ہسٹری' : 'Bank History'}
                    </button>
                  </div>

                  <div className={`flex items-stretch gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <div className="bg-white border border-emerald-200 rounded-md px-2 py-1">
                      <div className="text-[10px] text-gray-600">{isUrdu ? 'ابتدائی' : 'Opening'}</div>
                      <div className="text-xs font-bold text-brand-header">{selectedBank.openingBalance?.toLocaleString() || 0}</div>
                    </div>
                    <div className="bg-white border border-emerald-200 rounded-md px-2 py-1">
                      <div className="text-[10px] text-gray-600">{isUrdu ? 'موجودہ' : 'Current'}</div>
                      <div className={`text-xs font-bold ${currentBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {currentBalance.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 text-xs text-gray-500 print:hidden">
                {isUrdu ? 'بینک منتخب کریں تاکہ تفصیل نظر آئے' : 'Select a bank to view details'}
              </div>
            )}
          </div>

          {banks.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg border border-brand-card p-8 text-center animate-fade-in-up">
              <div className={`${isUrdu ? 'font-urdu' : ''}`}>
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <h3 className={`text-xl font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'کوئی ادائیگی کا طریقہ نہیں' : 'No Payment Methods Available'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {isUrdu ? 'ادائیگی کے طریقے شامل کریں (ایڈمن ڈیش بورڈ سے)' : 'Add payment methods (from admin dashboard)'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Payment Methods (Horizontal) */}
              <div className="print:hidden">
                <div className="bg-white rounded-lg shadow-lg border border-brand-card animate-fade-in-up">
                  <div className="p-2 border-b border-brand-card">
                    <div className={`flex justify-between items-center ${isUrdu ? 'flex-row-reverse' : ''}`}>
                      <h2 className={`text-sm font-semibold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'ادائیگی کے طریقے' : 'Payment Methods'}
                      </h2>
                      <div className="text-xs text-gray-500">
                        {banks.length}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`p-2 overflow-x-scroll bankkhata-hscroll ${isUrdu ? 'text-right' : 'text-left'}`}
                    style={{ scrollbarGutter: 'stable both-edges' }}
                  >
                    <div className={`flex gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`} style={{ minWidth: 'max-content' }}>
                      {banks.map((bank, index) => {
                        const bankBalance = calculateBankBalance(bank._id);
                        return (
                          <button
                            key={bank._id}
                            onClick={() => setSelectedBank(bank)}
                            className={`flex-shrink-0 min-w-[200px] text-right p-2 rounded-lg transition-all duration-200 ${selectedBank && selectedBank._id === bank._id
                                ? 'bg-gradient-to-br from-brand-header via-emerald-600 to-brand-header text-white shadow-xl ring-2 ring-emerald-200'
                                : 'bg-gradient-to-br from-white via-emerald-50 to-white hover:from-emerald-50 hover:to-emerald-100 text-gray-800 border border-emerald-200 hover:border-brand-header shadow-sm'
                              } ${isUrdu ? 'font-urdu' : ''}`}
                            style={{
                              animationDelay: `${index * 80}ms`,
                              animation: 'fadeInUp 0.4s ease-out'
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <div className={`p-1 rounded ${selectedBank && selectedBank._id === bank._id ? 'bg-white/20' : 'bg-brand-header/10'}`}>
                                    <svg className={`w-3.5 h-3.5 ${selectedBank && selectedBank._id === bank._id ? 'text-white' : 'text-brand-header'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                  </div>
                                  <div className="font-bold text-xs truncate">
                                    {bank.bankNameUrdu || bank.bankName}
                                  </div>
                                </div>
                                {bank.accountNumber && (
                                  <div className={`text-[10px] mt-0.5 truncate ${selectedBank && selectedBank._id === bank._id ? 'opacity-90' : 'opacity-70'}`}>
                                    {isUrdu ? 'اکاؤنٹ:' : 'A/C:'} {bank.accountNumber}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] opacity-80">{isUrdu ? 'بیلنس' : 'Bal.'}</div>
                                <div className={`text-xs font-bold ${bankBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {bankBalance.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Transactions Table - Full Width */}
          {selectedBank && (
            <div ref={printRef} className="bg-white rounded-lg shadow-lg border border-brand-card animate-fade-in-up mt-3">
              {/* Print Header - Company Info */}
              {company && (
                <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4 pdf-print-header">
                  <div className="flex items-start justify-between mb-2 gap-4">
                    {/* Company Name - Top Left */}
                    <div className="text-left flex-shrink-0">
                      <h1
                        className={`text-xl font-bold ${isUrdu ? 'font-urdu' : ''}`}
                        style={{
                          ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                          color: '#1e40af',
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact'
                        }}
                      >
                        {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
                      </h1>
                      {company?.username && (
                        <p className={`text-sm text-gray-600 font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'نام:' : 'Name:'} {company.username}
                        </p>
                      )}
                      {(company?.contactNumber || company?.phone || company?.mobile) && (
                        <p className="text-sm text-gray-700 font-semibold mt-1">
                          {isUrdu ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                        </p>
                      )}
                    </div>

                    {/* Logo - Top Right */}
                    <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
                      {company?.logo && (
                        <img
                          src={company.logo}
                          alt="Logo"
                          className="h-16 w-16 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  </div>
                  {company?.address && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <p className={`text-xs text-gray-700 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {company.address}
                      </p>
                    </div>
                  )}
                  {/* Bank and Date Range Info */}
                  <div className="mt-3 pt-2">
                    <h2 className={`text-lg font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                      {selectedBank.bankNameUrdu || selectedBank.bankName} - {isUrdu ? 'لین دین' : 'Transactions'}
                    </h2>
                    {(dateRange.startDate || dateRange.endDate) && (
                      <p className={`text-sm text-gray-600 text-center mt-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {dateRange.startDate && dateRange.endDate
                          ? `${isUrdu ? 'تاریخ:' : 'Date:'} ${new Date(dateRange.startDate).toLocaleDateString('en-GB')} ${isUrdu ? 'سے' : 'to'} ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`
                          : dateRange.startDate
                            ? `${isUrdu ? 'شروع:' : 'From:'} ${new Date(dateRange.startDate).toLocaleDateString('en-GB')}`
                            : dateRange.endDate
                              ? `${isUrdu ? 'تک:' : 'To:'} ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`
                              : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="p-4 border-b border-brand-card print:hidden">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h3 className={`text-lg font-semibold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'لین دین' : 'Transactions'}
                  </h3>
                  {/* Download and Print Buttons */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isGeneratingPDF ? (isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...') : (isUrdu ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
                    </button>
                    <button
                      onClick={handleDownloadExcel}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
                    </button>
                    <button
                      onClick={handlePrint}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {isUrdu ? 'پرنٹ' : 'Print'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Date Range Filter (moved above table) */}
              <div className="px-4 py-3 border-b border-gray-200 print:hidden">
                <div className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end ${isUrdu ? 'text-right' : 'text-left'}`}>
                  <div className="min-w-0">
                    <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'شروع کی تاریخ:' : 'Start Date:'}
                    </label>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => {
                        setDateRange({ ...dateRange, startDate: e.target.value });
                        if (selectedBank) {
                          setTimeout(() => fetchBankTransactions(selectedBank._id), 100);
                        }
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-header focus:border-transparent text-xs leading-tight"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'اختتام کی تاریخ:' : 'End Date:'}
                    </label>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => {
                        setDateRange({ ...dateRange, endDate: e.target.value });
                        if (selectedBank) {
                          setTimeout(() => fetchBankTransactions(selectedBank._id), 100);
                        }
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-header focus:border-transparent text-xs leading-tight"
                    />
                  </div>
                  <div className={`flex items-end gap-2 md:justify-self-end ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setDateRange({ startDate: '', endDate: '' });
                        if (selectedBank) {
                          setTimeout(() => fetchBankTransactions(selectedBank._id), 100);
                        }
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-3 py-1 rounded-lg transition-all text-xs"
                    >
                      {isUrdu ? 'کلیر' : 'Clear'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {transactions.length > 0 && (
                <div className={`px-4 py-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-gray-50 border-b border-gray-200 print:hidden ${isUrdu ? 'flex-row-reverse' : ''}`}>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                        onChange={handleSelectAll}
                        className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header"
                      />
                      <span className={`text-sm font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'سب منتخب کریں' : 'Select All'}
                      </span>
                    </label>
                    {selectedTransactions.length > 0 && (
                      <span className={`text-sm text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? `${selectedTransactions.length} منتخب` : `${selectedTransactions.length} selected`}
                      </span>
                    )}
                  </div>
                  {selectedTransactions.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      disabled={isDeleting}
                      className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isUrdu ? 'font-urdu' : ''}`}
                      style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {isDeleting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {isUrdu ? 'حذف ہو رہا ہے...' : 'Deleting...'}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {isUrdu ? `منتخب شدہ حذف کریں (${selectedTransactions.length})` : `Delete Selected (${selectedTransactions.length})`}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '1000px' }}>
                  <thead className="bg-brand-background">
                    <tr>
                      <th className={`px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card print:hidden ${isUrdu ? 'font-urdu' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                          onChange={handleSelectAll}
                          className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header"
                        />
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تاریخ' : 'Date'}
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'نام' : 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تفصیل' : 'Description'}
                      </th>
                      <th className={`px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'قسم' : 'Type'}
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'رقم' : 'Amount'}
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card whitespace-nowrap ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'بیلنس' : 'Balance'}
                      </th>
                      <th className={`px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card print:hidden ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'عمل' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className={`px-4 py-8 text-center text-gray-500 ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'کوئی لین دین نہیں' : 'No transactions'}
                        </td>
                      </tr>
                    ) : (
                      transactions.map((tx, index) => {
                        // Use original description for name extraction to get party name
                        const originalDesc = tx.originalDescription || tx.description;
                        const originalDescUrdu = tx.originalDescriptionUrdu || tx.descriptionUrdu;
                        const extractedName = extractNameFromDescription(originalDesc, originalDescUrdu);
                        const isSelected = selectedTransactions.includes(tx._id);
                        return (
                          <tr
                            key={tx._id}
                            className={`border-b border-brand-card hover:bg-brand-background transition-colors ${isSelected ? 'bg-blue-50' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')
                              }`}
                          >
                            <td className="px-4 py-3 text-center print:hidden">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleTransaction(tx._id)}
                                className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                              />
                            </td>
                            <td className={`px-4 py-3 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                              {new Date(tx.date).toLocaleDateString('en-GB')}
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                              {extractedName}
                            </td>
                            <td className={`px-4 py-3 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                              <div>
                                {isUrdu ? (tx.descriptionUrdu || tx.description || '-') : (tx.description || tx.descriptionUrdu || '-')}
                                {tx.recoveryId && (
                                  <div className="mt-1 print:hidden">
                                    <button
                                      onClick={() => navigate(`/recovery/view/${tx.recoveryId}`)}
                                      className="text-xs text-brand-header hover:text-emerald-800 underline"
                                    >
                                      {isUrdu ? 'ریکوری دیکھیں' : 'View Recovery'}
                                    </button>
                                  </div>
                                )}
                                {tx.expenseId && (
                                  <div className="mt-1 print:hidden">
                                    <button
                                      onClick={() => navigate(`/expense-ledger`)}
                                      className="text-xs text-brand-header hover:text-emerald-800 underline"
                                    >
                                      {isUrdu ? 'اخراجات دیکھیں' : 'View Expense'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${tx.type === 'deposit'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-red-100 text-red-700'
                                }`}>
                                {tx.type === 'deposit' ? (isUrdu ? 'جمع' : 'Deposit') : (isUrdu ? 'نکال' : 'Withdraw')}
                              </span>
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold ${tx.type === 'deposit' ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                              {tx.type === 'deposit' ? '+' : '-'}{tx.amount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                              {tx.balanceAfter ? tx.balanceAfter.toLocaleString() : '0'}
                            </td>
                            <td className="px-4 py-3 print:hidden">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleDeleteTransaction(tx._id)}
                                  className="px-3 py-1 bg-brand-alert hover:bg-red-600 text-white text-sm rounded transition-all transform hover:scale-105"
                                >
                                  {isUrdu ? 'حذف' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>


        {/* Add Balance Modal */}
        {showAddBalanceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'بیلنس شامل کریں' : 'Add Balance'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddBalanceModal(false);
                      setAddBalanceForm({
                        date: new Date().toLocaleDateString('en-CA'),
                        amount: '',
                        description: '',
                        descriptionUrdu: ''
                      });
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleAddBalance} className="space-y-4">
                  {/* Bank Name Display */}
                  {selectedBank && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                      <div className={`text-sm text-gray-600 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'بینک:' : 'Bank:'}
                      </div>
                      <div className={`text-lg font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {selectedBank.bankNameUrdu || selectedBank.bankName}
                      </div>
                      <div className={`text-sm text-gray-600 mt-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'موجودہ بیلنس:' : 'Current Balance:'} {calculateCurrentBalance().toLocaleString()}
                      </div>
                    </div>
                  )}

                  {/* Date Field */}
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={addBalanceForm.date}
                      onChange={(e) => setAddBalanceForm({ ...addBalanceForm, date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Amount Field */}
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'رقم' : 'Amount'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={addBalanceForm.amount}
                      onChange={(e) => setAddBalanceForm({ ...addBalanceForm, amount: e.target.value })}
                      required
                      className="w-full px-4 py-3 text-xl font-bold border-2 border-brand-header rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      placeholder="0.00"
                    />
                    {addBalanceForm.amount && parseFloat(addBalanceForm.amount) > 0 && selectedBank && (
                      <div className={`mt-2 text-sm text-emerald-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'نیا بیلنس:' : 'New Balance:'} {(calculateCurrentBalance() + parseFloat(addBalanceForm.amount)).toLocaleString()}
                      </div>
                    )}
                  </div>

                  {/* Description Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'تفصیل (انگریزی)' : 'Description (English)'}
                      </label>
                      <textarea
                        value={addBalanceForm.description}
                        onChange={(e) => setAddBalanceForm({ ...addBalanceForm, description: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                        placeholder={isUrdu ? 'بیلنس شامل کیا گیا' : 'Balance Added'}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'تفصیل (اردو)' : 'Description (Urdu)'}
                      </label>
                      <textarea
                        value={addBalanceForm.descriptionUrdu}
                        onChange={(e) => setAddBalanceForm({ ...addBalanceForm, descriptionUrdu: e.target.value })}
                        rows="2"
                        className={`w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all ${isUrdu ? 'font-urdu' : ''}`}
                        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                        placeholder="بیلنس شامل کیا گیا"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddBalanceModal(false);
                        setAddBalanceForm({
                          date: new Date().toLocaleDateString('en-CA'),
                          amount: '',
                          description: '',
                          descriptionUrdu: ''
                        });
                      }}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-all"
                    >
                      {isUrdu ? 'منسوخ' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white rounded-lg transition-all transform hover:scale-105"
                    >
                      {isUrdu ? 'بیلنس شامل کریں' : 'Add Balance'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* General Voucher Modal */}
        {showGeneralVoucherModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full border border-brand-card">
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h2 className={`text-lg font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'جنرل واؤچر' : 'General Voucher'}
                  </h2>
                  <button
                    onClick={() => setShowGeneralVoucherModal(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    type="button"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Flow preview */}
                <div className="mb-3 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50 text-sm">
                  <div className={`flex flex-wrap items-center gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`font-semibold text-purple-800 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'سمری:' : 'Summary:'}
                    </span>
                    <span className="text-gray-800">
                      {(() => {
                        const from = getAccountFromForm('from');
                        const to = getAccountFromForm('to');
                        const fromLabel = from.kind ? getAccountLabel(from.kind, from.id) : '-';
                        const toLabel = to.kind ? getAccountLabel(to.kind, to.id) : '-';
                        return `${fromLabel} → ${toLabel}`;
                      })()}
                    </span>
                    <span className="text-gray-600">
                      {(() => {
                        const from = getAccountFromForm('from');
                        const to = getAccountFromForm('to');
                        if (!from.kind || !to.kind) return '';
                        if (from.kind === 'bank' && to.kind === 'khata') return isUrdu ? '(بینک → کھاتہ)' : '(Bank → Khata)';
                        if (from.kind === 'khata' && to.kind === 'bank') return isUrdu ? '(کھاتہ → بینک)' : '(Khata → Bank)';
                        if (from.kind === 'bank' && to.kind === 'expense') return isUrdu ? '(بینک → اخراجات)' : '(Bank → Expense)';
                        return isUrdu ? '(غلط انتخاب)' : '(Invalid)';
                      })()}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleGeneralVoucherSubmit} className="space-y-3">
                  <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    {/* Left: From (Source) */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className={`flex items-center justify-between mb-2 gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                        <div className={`text-sm font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'جانبِ اوّل (وصول)' : 'Left (Receive)'}
                        </div>
                        <div className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                          {isUrdu ? 'جمع (Deposit)' : 'Deposit'}
                        </div>
                      </div>
                      <label className={`block text-xs text-gray-600 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>{isUrdu ? 'کھاتہ منتخب کریں' : 'Select type'}</label>
                      <select
                        value={generalVoucherForm.fromCategory}
                        onChange={(e) => {
                          const next = e.target.value;
                          setGeneralVoucherForm((prev) => ({
                            ...prev,
                            fromCategory: next,
                            fromId: next === 'bank' ? (selectedBank?._id || '') : ''
                          }));
                        }}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="bank">{isUrdu ? 'بینک کھاتہ' : 'Bank Ledger'}</option>
                        <option value="supplier">{isUrdu ? 'سپلائر کھاتہ' : 'Supplier Khata'}</option>
                        <option value="customer">{isUrdu ? 'گاہک کھاتہ' : 'Customer Khata'}</option>
                        <option value="receivable">{isUrdu ? 'قابل وصول کھاتہ' : 'Receivable Ledger'}</option>
                        <option value="payable">{isUrdu ? 'قابل ادائیگی کھاتہ' : 'Payable Ledger'}</option>
                        <option value="expense">{isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}</option>
                      </select>

                      {generalVoucherForm.fromCategory !== 'expense' && (
                        <>
                          <label className={`block text-xs text-gray-600 mt-2 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>{isUrdu ? 'سلیکٹ کریں' : 'Select'}</label>
                          <select
                            value={generalVoucherForm.fromId}
                            onChange={(e) => setGeneralVoucherForm((prev) => ({ ...prev, fromId: e.target.value }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          >
                            <option value="">{isUrdu ? 'منتخب کریں' : 'Select'}</option>
                            {generalVoucherForm.fromCategory === 'bank' &&
                              banks.map((b) => (
                                <option key={`bank-${b._id}`} value={b._id}>
                                  {isUrdu ? (b.bankNameUrdu || b.nameUrdu || b.bankName || b.name) : (b.bankName || b.name)}
                                </option>
                              ))}
                            {generalVoucherForm.fromCategory === 'supplier' &&
                              ledgerParties.filter((p) => p.type === 'supplier').map((p) => (
                                <option key={`sup-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.fromCategory === 'customer' &&
                              ledgerParties.filter((p) => p.type === 'customer').map((p) => (
                                <option key={`cus-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.fromCategory === 'receivable' &&
                              ledgerParties.filter((p) => (p.currentBalance || 0) > 0).map((p) => (
                                <option key={`recv-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.fromCategory === 'payable' &&
                              ledgerParties.filter((p) => (p.currentBalance || 0) < 0).map((p) => (
                                <option key={`pay-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                          </select>
                        </>
                      )}
                    </div>

                    {/* Middle: Date */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-col justify-between">
                      <div>
                        <div className={`text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </div>
                        <div className="text-xs text-gray-600">
                          {isUrdu ? 'سورس/منزل کے حساب سے جمع/نکال خود بنے گا' : 'Deposit/Withdraw will auto-decide from Source/Destination'}
                        </div>
                      </div>

                      <div className="mt-3">
                        <input
                          type="date"
                          value={generalVoucherForm.date}
                          onChange={(e) => setGeneralVoucherForm({ ...generalVoucherForm, date: e.target.value })}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                          required
                        />
                      </div>
                    </div>

                    {/* Right: To (Destination) */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className={`flex items-center justify-between mb-2 gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                        <div className={`text-sm font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'جانبِ دوم (ارسال)' : 'Right (Send)'}
                        </div>
                        <div className="text-[11px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                          {isUrdu ? 'نکال (Withdraw)' : 'Withdraw'}
                        </div>
                      </div>

                      <label className={`block text-xs text-gray-600 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>{isUrdu ? 'کھاتہ منتخب کریں' : 'Select type'}</label>
                      <select
                        value={generalVoucherForm.toCategory}
                        onChange={(e) => {
                          const next = e.target.value;
                          setGeneralVoucherForm((prev) => ({
                            ...prev,
                            toCategory: next,
                            toId: ''
                          }));
                        }}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="supplier">{isUrdu ? 'سپلائر کھاتہ' : 'Supplier Khata'}</option>
                        <option value="customer">{isUrdu ? 'گاہک کھاتہ' : 'Customer Khata'}</option>
                        <option value="receivable">{isUrdu ? 'قابل وصول کھاتہ' : 'Receivable Ledger'}</option>
                        <option value="payable">{isUrdu ? 'قابل ادائیگی کھاتہ' : 'Payable Ledger'}</option>
                        <option value="bank">{isUrdu ? 'بینک کھاتہ' : 'Bank Ledger'}</option>
                        <option value="expense">{isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}</option>
                      </select>

                      {generalVoucherForm.toCategory !== 'expense' && (
                        <>
                          <label className={`block text-xs text-gray-600 mt-2 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>{isUrdu ? 'سلیکٹ کریں' : 'Select'}</label>
                          <select
                            value={generalVoucherForm.toId}
                            onChange={(e) => setGeneralVoucherForm((prev) => ({ ...prev, toId: e.target.value }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          >
                            <option value="">{isUrdu ? 'منتخب کریں' : 'Select'}</option>
                            {generalVoucherForm.toCategory === 'bank' &&
                              banks.map((b) => (
                                <option key={`bank-${b._id}`} value={b._id}>
                                  {isUrdu ? (b.bankNameUrdu || b.nameUrdu || b.bankName || b.name) : (b.bankName || b.name)}
                                </option>
                              ))}
                            {generalVoucherForm.toCategory === 'supplier' &&
                              ledgerParties.filter((p) => p.type === 'supplier').map((p) => (
                                <option key={`sup-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.toCategory === 'customer' &&
                              ledgerParties.filter((p) => p.type === 'customer').map((p) => (
                                <option key={`cus-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.toCategory === 'receivable' &&
                              ledgerParties.filter((p) => (p.currentBalance || 0) > 0).map((p) => (
                                <option key={`recv-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                            {generalVoucherForm.toCategory === 'payable' &&
                              ledgerParties.filter((p) => (p.currentBalance || 0) < 0).map((p) => (
                                <option key={`pay-${p._id}`} value={p._id}>
                                  {isUrdu ? (p.nameUrdu || p.name) : (p.name || p.nameUrdu)}
                                </option>
                              ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expense type (only when selecting company expense ledger) */}
                  {(() => {
                    const usingExpense = generalVoucherForm.fromCategory === 'expense' || generalVoucherForm.toCategory === 'expense';
                    if (!usingExpense) return null;
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className={`text-sm font-semibold text-amber-900 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'اخراجات کی قسم' : 'Expense Type'}
                        </div>
                        <select
                          value={generalVoucherForm.expenseType}
                          onChange={(e) => setGeneralVoucherForm({ ...generalVoucherForm, expenseType: e.target.value })}
                          className="w-full max-w-sm px-2 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                          required
                        >
                          <option value="travellingExpense">{isUrdu ? 'سفر خرچ' : 'Travelling'}</option>
                          <option value="refreshment">{isUrdu ? 'چائے پانی' : 'Refreshment'}</option>
                          <option value="cargo">{isUrdu ? 'کارگو' : 'Cargo'}</option>
                          <option value="salaryExpense">{isUrdu ? 'تنخواہ' : 'Salary'}</option>
                        </select>
                        <div className="mt-1 text-xs text-amber-800">
                          {isUrdu ? 'نوٹ: کمپنی اخراجات کیلئے صرف بینک سے اخراجات (Bank → Expense) ہوگا' : 'Note: Company expense supports Bank → Expense only'}
                        </div>
                      </div>
                    );
                  })()}

                  <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <div>
                      <label className={`block text-xs text-gray-600 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'رقم' : 'Amount'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={generalVoucherForm.amount}
                        onChange={(e) => setGeneralVoucherForm({ ...generalVoucherForm, amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={`block text-xs text-gray-600 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تفصیل (اختیاری)' : 'Description (optional)'}
                      </label>
                      <input
                        type="text"
                        value={generalVoucherForm.description}
                        onChange={(e) => setGeneralVoucherForm({ ...generalVoucherForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder={isUrdu ? 'مثال: جنرل واؤچر' : 'e.g. General Voucher'}
                      />
                    </div>
                  </div>

                  <div className={`flex items-center justify-end gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setShowGeneralVoucherModal(false)}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm rounded-lg"
                    >
                      {isUrdu ? 'بند کریں' : 'Close'}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white text-sm font-semibold rounded-lg"
                    >
                      {isUrdu ? 'محفوظ کریں' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add Transaction Modal */}
        {showAddTransactionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'بینک لین دین' : 'Bank Transaction'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddTransactionModal(false);
                      setEditingTransaction(null);
                      resetTransactionForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleTransactionSubmit} className="space-y-4">
                  {/* Date Field */}
                  <div className="mb-4">
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={transactionForm.date}
                      onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                      required
                      className="w-full max-w-xs px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Three Column Layout */}
                  <div className="grid grid-cols-3 gap-6">
                    {/* Left Side - Source Banks */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className={`text-lg font-semibold text-brand-header mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'بینک منتخب کریں (سورس)' : 'Select Bank (Source)'}
                      </h3>
                      <div className="max-h-[400px] overflow-y-auto space-y-2">
                        {paymentMethods.filter(pm => pm.isActive).map((bank) => {
                          const bankBalance = calculateBankBalance(bank._id);
                          const amountNum = parseFloat(transactionForm.amount) || 0;
                          const isSelected = transactionForm.fromBank === bank._id;
                          const balanceAfterWithdraw = isSelected && amountNum > 0 && transactionForm.type !== 'deposit'
                            ? bankBalance - amountNum
                            : (isSelected && amountNum > 0 && transactionForm.type === 'deposit'
                              ? bankBalance - amountNum
                              : bankBalance);
                          return (
                            <button
                              key={bank._id}
                              type="button"
                              onClick={() => setTransactionForm({ ...transactionForm, fromBank: bank._id })}
                              className={`w-full text-right p-3 rounded-lg transition-all ${isSelected
                                  ? 'bg-brand-header text-white shadow-md'
                                  : 'bg-white hover:bg-gray-100 border border-gray-300'
                                } ${isUrdu ? 'font-urdu' : ''}`}
                              style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                            >
                              <div className="font-semibold">{isUrdu ? (bank.nameUrdu || bank.name) : bank.name}</div>
                              <div className={`text-xs mt-1 ${isSelected ? 'text-white/90' : 'text-gray-600'}`}>
                                {isUrdu ? 'بیلنس:' : 'Balance:'} {bankBalance.toLocaleString()}
                              </div>
                              {isSelected && amountNum > 0 && (
                                <div className={`text-xs mt-1 ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>
                                  {transactionForm.type === 'deposit'
                                    ? (isUrdu ? 'ٹرانسفر کے بعد:' : 'After Transfer:')
                                    : (isUrdu ? 'نکالنے کے بعد:' : 'After Withdraw:')} {balanceAfterWithdraw.toLocaleString()}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Middle - Amount Field */}
                    <div className="flex flex-col items-center justify-center bg-gradient-to-br from-brand-header to-emerald-700 rounded-lg p-6 border border-brand-card">
                      <label className={`block text-sm font-medium text-white mb-3 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'رقم' : 'Amount'} <span className="text-red-300">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={transactionForm.amount}
                        onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                        required
                        className="w-full px-4 py-3 text-2xl font-bold text-center border-2 border-white rounded-lg focus:ring-2 focus:ring-white focus:border-transparent transition-all bg-white text-brand-header"
                        placeholder="0"
                      />
                      {transactionForm.fromBank && (() => {
                        const balance = calculateBankBalance(transactionForm.fromBank);
                        const amountNum = parseFloat(transactionForm.amount) || 0;
                        const balanceAfterTransfer = transactionForm.type === 'withdraw'
                          ? balance - amountNum
                          : (transactionForm.type === 'deposit' && transactionForm.toBank ? balance - amountNum : balance);
                        return (
                          <div className="mt-4 space-y-2">
                            <div className={`text-white text-sm text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'موجودہ بیلنس:' : 'Current Balance:'} {balance.toLocaleString()}
                            </div>
                            {amountNum > 0 && (
                              <div className={`text-white/80 text-xs text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {transactionForm.type === 'deposit' && transactionForm.toBank
                                  ? (isUrdu ? 'ٹرانسفر کے بعد:' : 'After Transfer:')
                                  : (isUrdu ? 'نکالنے کے بعد:' : 'After Withdraw:')} {balanceAfterTransfer.toLocaleString()}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Right Side - Deposit/Withdraw */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className={`text-lg font-semibold text-brand-header mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'جمع/نکال' : 'Deposit/Withdraw'}
                      </h3>

                      {/* Type Selection */}
                      <div className="mb-4">
                        <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'قسم' : 'Type'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={transactionForm.type}
                          onChange={(e) => setTransactionForm({ ...transactionForm, type: e.target.value, toBank: '' })}
                          required
                          className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                        >
                          <option value="deposit">{isUrdu ? 'جمع' : 'Deposit'}</option>
                          <option value="withdraw">{isUrdu ? 'نکال' : 'Withdraw'}</option>
                        </select>
                      </div>

                      {/* Destination Bank Selection */}
                      {transactionForm.type === 'deposit' && (
                        <div>
                          <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {isUrdu ? 'بینک منتخب کریں (منزل)' : 'Select Bank (Destination)'} <span className="text-red-500">*</span>
                          </label>
                          <div className="max-h-[300px] overflow-y-auto space-y-2">
                            {paymentMethods.filter(pm => pm.isActive && pm._id !== transactionForm.fromBank).map((bank) => {
                              const bankBalance = calculateBankBalance(bank._id);
                              const amountNum = parseFloat(transactionForm.amount) || 0;
                              const balanceAfterDeposit = transactionForm.toBank === bank._id && amountNum > 0
                                ? bankBalance + amountNum
                                : bankBalance;
                              return (
                                <button
                                  key={bank._id}
                                  type="button"
                                  onClick={() => setTransactionForm({ ...transactionForm, toBank: bank._id })}
                                  className={`w-full text-right p-3 rounded-lg transition-all ${transactionForm.toBank === bank._id
                                      ? 'bg-emerald-600 text-white shadow-md'
                                      : 'bg-white hover:bg-gray-100 border border-gray-300'
                                    } ${isUrdu ? 'font-urdu' : ''}`}
                                  style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                                >
                                  <div className="font-semibold">{isUrdu ? (bank.nameUrdu || bank.name) : bank.name}</div>
                                  <div className={`text-xs mt-1 ${transactionForm.toBank === bank._id ? 'text-white/90' : 'text-gray-600'}`}>
                                    {isUrdu ? 'بیلنس:' : 'Balance:'} {bankBalance.toLocaleString()}
                                  </div>
                                  {transactionForm.toBank === bank._id && amountNum > 0 && (
                                    <div className={`text-xs mt-1 ${transactionForm.toBank === bank._id ? 'text-white/70' : 'text-gray-500'}`}>
                                      {isUrdu ? 'جمع کے بعد:' : 'After Deposit:'} {balanceAfterDeposit.toLocaleString()}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {transactionForm.type === 'withdraw' && (
                        <div className={`text-center py-8 text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'رقم منتخب بینک سے نکال دی جائے گی' : 'Amount will be withdrawn from selected bank'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description Fields */}
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'تفصیل (انگریزی)' : 'Description (English)'}
                      </label>
                      <textarea
                        value={transactionForm.description}
                        onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'تفصیل (اردو)' : 'Description (Urdu)'}
                      </label>
                      <textarea
                        value={transactionForm.descriptionUrdu}
                        onChange={(e) => setTransactionForm({ ...transactionForm, descriptionUrdu: e.target.value })}
                        rows="2"
                        className={`w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all ${isUrdu ? 'font-urdu' : ''}`}
                        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddTransactionModal(false);
                        setEditingTransaction(null);
                        resetTransactionForm();
                      }}
                      className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-all transform hover:scale-105"
                    >
                      {isUrdu ? 'منسوخ' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      disabled={!transactionForm.fromBank || !transactionForm.amount || (transactionForm.type === 'deposit' && !transactionForm.toBank)}
                      className="px-6 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isUrdu ? 'ٹرانسفر کریں' : 'Transfer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Inter-Bank Excel View Modal */}
        {showInterBankExcelView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'بینک لین دین Excel View' : 'Inter-Bank Transactions Excel View'}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportInterBankExcel}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Export Excel'}
                    </button>
                    <button
                      onClick={() => setShowInterBankExcelView(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Excel View Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-300">
                    <thead>
                      <tr>
                        <th colSpan="4" className="bg-emerald-600 text-white px-4 py-3 text-center border border-gray-300 font-bold">
                          {isUrdu ? 'نکال (Withdrawals)' : 'Withdrawals'}
                        </th>
                        <th className="bg-gray-200 border border-gray-300"></th>
                        <th colSpan="4" className="bg-emerald-700 text-white px-4 py-3 text-center border border-gray-300 font-bold">
                          {isUrdu ? 'جمع (Deposits)' : 'Deposits'}
                        </th>
                      </tr>
                      <tr className="bg-gray-100">
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'بینک' : 'Bank'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'رقم' : 'Amount'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تفصیل' : 'Description'}
                        </th>
                        <th className="bg-gray-200 border border-gray-300"></th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'بینک' : 'Bank'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'رقم' : 'Amount'}
                        </th>
                        <th className={`px-4 py-2 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تفصیل' : 'Description'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Separate withdrawals and deposits
                        const withdrawals = [];
                        const deposits = [];

                        interBankTransactions.forEach(tx => {
                          const fromBankName = paymentMethods.find(pm => pm._id === tx.fromBank)?.nameUrdu ||
                            paymentMethods.find(pm => pm._id === tx.fromBank)?.name ||
                            'Unknown';
                          const toBankName = paymentMethods.find(pm => pm._id === tx.toBank)?.nameUrdu ||
                            paymentMethods.find(pm => pm._id === tx.toBank)?.name ||
                            'Unknown';

                          withdrawals.push({
                            date: tx.date,
                            bank: fromBankName,
                            amount: tx.amount,
                            description: tx.descriptionUrdu || tx.description || ''
                          });

                          deposits.push({
                            date: tx.date,
                            bank: toBankName,
                            amount: tx.amount,
                            description: tx.descriptionUrdu || tx.description || ''
                          });
                        });

                        // Sort by date
                        withdrawals.sort((a, b) => new Date(a.date) - new Date(b.date));
                        deposits.sort((a, b) => new Date(a.date) - new Date(b.date));

                        const maxRows = Math.max(withdrawals.length, deposits.length);
                        const rows = [];

                        for (let i = 0; i < maxRows; i++) {
                          const withdraw = withdrawals[i];
                          const deposit = deposits[i];
                          rows.push({ withdraw, deposit });
                        }

                        return rows.map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.withdraw ? new Date(row.withdraw.date).toLocaleDateString('en-GB') : ''}
                            </td>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.withdraw ? row.withdraw.bank : ''}
                            </td>
                            <td className="px-4 py-2 border border-gray-300 text-right font-semibold text-red-600">
                              {row.withdraw ? row.withdraw.amount.toLocaleString() : ''}
                            </td>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.withdraw ? row.withdraw.description : ''}
                            </td>
                            <td className="bg-gray-200 border border-gray-300"></td>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.deposit ? new Date(row.deposit.date).toLocaleDateString('en-GB') : ''}
                            </td>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.deposit ? row.deposit.bank : ''}
                            </td>
                            <td className="px-4 py-2 border border-gray-300 text-right font-semibold text-emerald-600">
                              {row.deposit ? row.deposit.amount.toLocaleString() : ''}
                            </td>
                            <td className={`px-4 py-2 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {row.deposit ? row.deposit.description : ''}
                            </td>
                          </tr>
                        ));
                      })()}
                      {/* Totals Row */}
                      <tr className="bg-gray-200 font-bold">
                        <td className={`px-4 py-3 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'کل' : 'Total'}
                        </td>
                        <td className="px-4 py-3 border border-gray-300"></td>
                        <td className="px-4 py-3 border border-gray-300 text-right font-semibold text-red-600">
                          {interBankTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border border-gray-300"></td>
                        <td className="bg-gray-200 border border-gray-300"></td>
                        <td className={`px-4 py-3 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'کل' : 'Total'}
                        </td>
                        <td className="px-4 py-3 border border-gray-300"></td>
                        <td className="px-4 py-3 border border-gray-300 text-right font-semibold text-emerald-600">
                          {interBankTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border border-gray-300"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Add Inter-Bank Transaction Button */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setShowInterBankExcelView(false);
                      setShowInterBankModal(true);
                    }}
                    className="px-6 py-2 bg-brand-header hover:bg-emerald-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {isUrdu ? 'نیا بینک لین دین' : 'Add Inter-Bank Transaction'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bank History Modal */}
        {showBankHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'بینک کی تاریخ' : 'Bank History'}
                  </h2>
                  <button
                    onClick={() => setShowBankHistory(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Bank History Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-brand-header text-white">
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'نام' : 'Name'}
                        </th>
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'بینک' : 'Bank'}
                        </th>
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'تفصیل' : 'Description'}
                        </th>
                        <th className={`px-4 py-3 text-center border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'قسم' : 'Type'}
                        </th>
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'رقم' : 'Amount'}
                        </th>
                        <th className={`px-4 py-3 text-right border border-gray-300 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'بیلنس' : 'Balance'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankHistoryTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className={`px-4 py-8 text-center text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {isUrdu ? 'کوئی لین دین نہیں' : 'No transactions found'}
                          </td>
                        </tr>
                      ) : (
                        bankHistoryTransactions.map((tx, index) => {
                          // Use original description for name extraction to get party name
                          const originalDesc = tx.originalDescription || tx.description;
                          const originalDescUrdu = tx.originalDescriptionUrdu || tx.descriptionUrdu;
                          const extractedName = extractNameFromDescription(originalDesc, originalDescUrdu);
                          return (
                            <tr key={tx._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className={`px-4 py-3 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {new Date(tx.date).toLocaleDateString('en-GB')}
                              </td>
                              <td className={`px-4 py-3 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {extractedName}
                              </td>
                              <td className={`px-4 py-3 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {tx.paymentMethod || '-'}
                              </td>
                              <td className={`px-4 py-3 border border-gray-300 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {isUrdu ? (tx.descriptionUrdu || tx.description || '-') : (tx.description || tx.descriptionUrdu || '-')}
                              </td>
                              <td className="px-4 py-3 border border-gray-300 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${tx.type === 'deposit'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-red-100 text-red-700'
                                  }`}>
                                  {tx.type === 'deposit' ? (isUrdu ? 'جمع' : 'Deposit') : (isUrdu ? 'نکال' : 'Withdraw')}
                                </span>
                              </td>
                              <td className={`px-4 py-3 border border-gray-300 text-right font-semibold ${tx.type === 'deposit' ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                {tx.type === 'deposit' ? '+' : '-'}{(tx.amount || 0).toLocaleString()}
                              </td>
                              <td className={`px-4 py-3 border border-gray-300 text-right font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {(tx.balanceAfter || 0).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inter-Bank Transaction Modal */}
        {showInterBankModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'نیا بینک لین دین' : 'Add Inter-Bank Transaction'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowInterBankModal(false);
                      setInterBankForm({
                        date: new Date().toLocaleDateString('en-CA'),
                        fromBank: '',
                        toBank: '',
                        amount: '',
                        description: '',
                        descriptionUrdu: ''
                      });
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleAddInterBankTransaction} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={interBankForm.date}
                      onChange={(e) => setInterBankForm({ ...interBankForm, date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'بینک سے (نکال)' : 'From Bank (Withdraw)'} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={interBankForm.fromBank}
                      onChange={(e) => setInterBankForm({ ...interBankForm, fromBank: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    >
                      <option value="">{isUrdu ? 'منتخب کریں' : 'Select Bank'}</option>
                      {paymentMethods.filter(pm => pm.isActive).map(pm => (
                        <option key={pm._id} value={pm._id}>
                          {isUrdu ? (pm.nameUrdu || pm.name) : pm.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'بینک میں (جمع)' : 'To Bank (Deposit)'} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={interBankForm.toBank}
                      onChange={(e) => setInterBankForm({ ...interBankForm, toBank: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    >
                      <option value="">{isUrdu ? 'منتخب کریں' : 'Select Bank'}</option>
                      {paymentMethods.filter(pm => pm.isActive && pm._id !== interBankForm.fromBank).map(pm => (
                        <option key={pm._id} value={pm._id}>
                          {isUrdu ? (pm.nameUrdu || pm.name) : pm.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'رقم' : 'Amount'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={interBankForm.amount}
                      onChange={(e) => setInterBankForm({ ...interBankForm, amount: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تفصیل (انگریزی)' : 'Description (English)'}
                    </label>
                    <textarea
                      value={interBankForm.description}
                      onChange={(e) => setInterBankForm({ ...interBankForm, description: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'تفصیل (اردو)' : 'Description (Urdu)'}
                    </label>
                    <textarea
                      value={interBankForm.descriptionUrdu}
                      onChange={(e) => setInterBankForm({ ...interBankForm, descriptionUrdu: e.target.value })}
                      rows="2"
                      className={`w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all ${isUrdu ? 'font-urdu' : ''}`}
                      style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInterBankModal(false);
                        setInterBankForm({
                          date: new Date().toLocaleDateString('en-CA'),
                          fromBank: '',
                          toBank: '',
                          amount: '',
                          description: '',
                          descriptionUrdu: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-all transform hover:scale-105"
                    >
                      {isUrdu ? 'منسوخ' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-lg"
                    >
                      {isUrdu ? 'محفوظ کریں' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        /* Horizontal scrollbar styling for payment methods bar */
        .bankkhata-hscroll { padding-bottom: 6px; }
        .bankkhata-hscroll::-webkit-scrollbar { height: 10px; }
        .bankkhata-hscroll::-webkit-scrollbar-track { background: #e5e7eb; border-radius: 9999px; }
        .bankkhata-hscroll::-webkit-scrollbar-thumb { background: #16a34a; border-radius: 9999px; }
        .bankkhata-hscroll::-webkit-scrollbar-thumb:hover { background: #15803d; }
        .bankkhata-hscroll { scrollbar-color: #16a34a #e5e7eb; scrollbar-width: thin; }

        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .bg-gradient-to-r, .bg-gradient-to-br {
            background: #059669 !important;
          }
          .bg-brand-background {
            background: white !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-xl, .rounded-lg {
            border-radius: 0 !important;
          }
          .shadow-lg, .shadow-2xl {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
          div[ref] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </>
  );
}

export default BankKhata;

